/* ==========================================================================
   <nex-gdab> — Global Dynamic Action Button (build 2, post-audit)
   State machine per the Figma rule doc (node 260:576), rotation + confirm
   flow reworked per Jackson (Aug 12):

     pill ──every rotation beat──▶ next slot of [price, msg1 … msgN]
                                   (ONE flat forever-cycle; interaction
                                   never resets or reverts it)
     pill ──tap Pre-order/Buy──▶ panel (color selection, 30% scrim)
     panel ──Add to cart──▶ cart opens INSTANTLY with a pending line ∥
                            the add POST runs in parallel
     header chip ──tap──▶ cart opens INSTANTLY on the best local truth
                          (cached cart, else skeleton rows in the pending
                          dress) ∥ /cart.js reconciles in place
     cart ──close/scrim/ESC──▶ pill (rotation resumes where it left off)

   Audit-driven invariants:
   - The pill is a real <button type=submit> inside a real product form; this
     component only ever enhances it. No JS → native ATC.
   - Two failure domains: the add POST failing turns the pending line into
     a retryable error INSIDE the open cart — totals, badge and checkout
     fall back to server truth, so a failed line can never read as
     purchased. A cart-refresh failing AFTER a successful add never says
     "couldn't add" (that invites double-orders) — the sheet shows
     locally-known data instead.
   - A generation counter guards every async continuation: closing the panel
     mid-flight must not force the cart open later.
   - All external resources (window listeners, observers, timers) are armed
     on connect and disarmed on disconnect — the theme editor re-renders
     sections repeatedly and must not multiply them.
   ========================================================================== */
(() => {
  "use strict";
  if (customElements.get("nex-gdab")) return;

  const FOCUSABLE = [
    'button:not([disabled]):not([tabindex="-1"])',
    'a[href]:not([tabindex="-1"])',
    'input:not([disabled]):not([tabindex="-1"])',
    'select:not([disabled]):not([tabindex="-1"])',
    'textarea:not([disabled]):not([tabindex="-1"])',
    '[tabindex]:not([tabindex="-1"]):not([disabled])',
  ].join(", ");
  const isVisible = (n) => (typeof n.checkVisibility === "function"
    // both spellings: engines that shipped only the ORIGINAL dictionary
    // keys (checkVisibilityCSS/checkOpacity — Chrome 105-120 era) ignore
    // the renamed ones and would re-admit opacity-0 controls to the trap
    ? n.checkVisibility({ visibilityProperty: true, opacityProperty: true,
        checkVisibilityCSS: true, checkOpacity: true })
    : n.getClientRects().length > 0);

  // DOM-level singleton: the class guard above stops a double DEFINE, but
  // nothing stopped a double INSTANCE (theme editor: section added twice).
  // Two live GDABs both answer gdab:open-cart, and each one's lockPage()
  // inerts the OTHER — two open carts whose close buttons are both inert,
  // Escape reaches neither, scroll lock stuck (QA F4). The first connected
  // instance owns the page; later ones park (CSS hides them) until the
  // owner disconnects.
  let ACTIVE = null;

  class NexGdab extends HTMLElement {
    connectedCallback() {
      if (ACTIVE && ACTIVE !== this && ACTIVE.isConnected) {
        this.setAttribute("data-gdab-duplicate", "");
        return;                          // parked: no init, no listeners
      }
      ACTIVE = this;
      this.removeAttribute("data-gdab-duplicate");
      if (!this.__init) {
        this.__init = true;
        this.initOnce();
      }
      this.armExternal();
    }
    disconnectedCallback() {
      if (ACTIVE !== this) return;       // parked duplicate: nothing armed
      ACTIVE = null;
      this.disarmExternal();
      // a parked duplicate may take over — but only NEXT tick, so an
      // editor re-render's replacement node (which reconnects within this
      // same task) claims ownership first and the edited section wins
      setTimeout(() => {
        if (ACTIVE) return;
        const next = document.querySelector("nex-gdab");
        if (next && next.isConnected) next.connectedCallback();
      }, 0);
    }

    initOnce() {
      const attr = (n) => this.getAttribute(n);
      this.cfg = {
        addUrl: attr("data-add-url") || "/cart/add.js",
        cartUrl: attr("data-cart-url") || "/cart.js",
        cartPageUrl: attr("data-cart-page-url") || "/cart",
        cartCheckError: attr("data-cart-check-error") || "We could not confirm your cart. Please try again.",
        changeUrl: attr("data-change-url") || "/cart/change.js",
        checkoutUrl: attr("data-checkout-url") || "/checkout",
        imgOrange: attr("data-img-orange") || "",
        imgBlack: attr("data-img-black") || "",
        imgDock: attr("data-img-dock") || "",
        dockVariantId: attr("data-dock-variant-id") || "",
        dynamicText: attr("data-dynamic-text") !== "false",
        surfaceForce: attr("data-surface") || "",
        moneyFmt: attr("data-money-format") || "${{amount}}",
        sellState: attr("data-sell-state") === "preorder" ? "preorder" : "stock",
        // the ONE rotation cadence (Jackson, Aug 12 — the idle/browse/
        // video trigger clocks and the sweep-gap throttle are gone)
        ambientMs: (parseInt(attr("data-ambient-seconds"), 10) || 7) * 1000,
      };
      const parse = (name, fallback) => {
        try { return JSON.parse(attr(name) || fallback); } catch (e) { return JSON.parse(fallback); }
      };
      const phraseSeen = new Set();
      this.phrases = parse("data-phrases", "[]")
        .map((p) => String(p).trim())
        .filter((p) => {
          const key = p.toLocaleLowerCase();
          if (!p || phraseSeen.has(key)) return false;
          phraseSeen.add(key);
          return true;
        });
      this._phraseBag = [];
      this._lastPhrase = "";
      // variantId(str) -> compare-at cents; emitted by Liquid ONLY when the
      // strike gate (preorder && show_strike && real compare) is on, so the
      // cart's strikethrough obeys the same gate as every other surface.
      this.compareMap = {};
      // variantId -> per-line stock label ("3 available"), mode-aware
      this.stockMap = parse("data-stock-map", "{}");
      // variantId -> { preorder, tail }. Liquid resolves the metafield-backed
      // promise; JS chooses from the ACTUAL /cart.js lines so mixed carts do
      // not inherit whichever colour the pill advertised on page load.
      this.noteMap = parse("data-note-map", "{}");
      // trust messages: [{id, text, when}] — when gates by sell state
      // (always | preorder | stock); the low-inventory gate is applied
      // Liquid-side before the pool ever reaches JS
      this.allMessages = parse("data-messages", "[]").filter((m) => m && m.text);
      this.messages = this.allMessages.filter((m) =>
        !m.when || m.when === "always" || m.when === this.cfg.sellState);

      this.$ = (sel) => this.querySelector(sel);
      this.pill = this.$(".gdab__pill");
      this.trustEl = this.$(".gdab__trust");
      this.form = this.$("form.gdab__form") || null;
      this.nexie = this.$(".gdab__nexie");
      this.panel = this.$(".gdab__panel");
      this.cart = this.$(".gdab__cart");
      this.scrim = this.$(".gdab__scrim");
      this.confirmBtn = this.$(".gdab__confirm");
      this.checkoutForm = this.$(".gdab__checkout-form");
      this.checkoutError = this.$(".gdab__checkout-error");
      this.confirmRow = this.$(".gdab__confirm-row");
      this.buyShell = this.$(".gdab__buy");
      this.reviewProof = this.$("[data-gdab-review-proof]");
      this.reviewCopy = this.$("[data-gdab-review-copy]");
      this.reviewText = this.$("[data-gdab-review-text]") || this.reviewCopy;
      this.reviewApi = this.reviewProof
        ? this.reviewProof.getAttribute("data-review-api") || ""
        : "";
      this.liveRoot = this.$(".gdab__live");
      this.livePanel = this.panel.querySelector(".gdab__live-dialog");
      this.liveCart = this.cart.querySelector(".gdab__live-dialog");

      this.state = "pill";
      this.content = "offer";
      this.selectedVariant = null;
      this.lastFocus = null;
      this.cycleIdx = 0;         // rotation slot: 0 = price, k = message k
      this.reviews = [];
      this.reviewIndex = 0;
      this._reviewLoadPromise = null;
      this._pending = null;      // { variant, key, failed? } while an
                                 // optimistic add is unresolved
      this._changeRetry = null;  // { key, quantity, focusClass } after a
                                 // visible/retryable quantity failure
      this._skeleton = false;    // header-open placeholders on screen
      this._skelFailed = false;  // ...and their refresh died (retry state)
      this._truthSeq = 0;        // bumped per confirmed render; a stale
                                 // header refresh must never repaint over
                                 // newer truth (e.g. a qty change's answer)
      this.gen = 0;              // bumped on every session exit; async
                                 // continuations compare before acting
      this._closing = false;     // visual panel→pill handoff in progress
      this._closeT = 0;          // timeout belt for a lost animationend
      this._closeVisual = null;  // inert FLIP layer (never a payment control)
      this._closeCta = null;
      this._closeEndHandler = null;
      this._closePanelEndHandler = null;
      this._closeFinishFrame = 0;
      this._closeFinishFrame2 = 0;
      this._closeAnimationDone = false;
      this._closeClipDone = false;
      this._closeSettleFrame = 0;
      this._closeSettleFrame2 = 0;

      // the CHOOSE-COLOR invite is a JS enhancement (Jackson, Aug 26): the
      // server renders the truthful mode chip because with dead scripts
      // the pill is a NATIVE add-to-cart — an invite label there would lie
      // on the money path. With JS alive the swap happens here, under the
      // entrance veil, before the tick baselines seed. Old cached HTML
      // (no attr / no data-t span) degrades to the truthful chip.
      if (this.hasAttribute("data-gdab-choose") &&
          !this.$(".gdab__card[data-route-selected]")) {
        const chooseChip = this.$(".gdab__pill .gdab__chip");
        const chooseLab = this.$(".gdab__pill .gdab__chip-label");
        const invite = this.t("chooseColor");
        if (chooseChip && chooseLab && invite !== "chooseColor") {
          chooseChip.classList.add("gdab__chip--choose");
          chooseLab.textContent = invite;
        }
      }
      // seed ticker baselines from the server-rendered content (<br> and
      // newlines normalize to spaces) — without this, the FIRST chip tick
      // rolled visually identical text (QA polish find)
      [".gdab__pill .gdab__chip-label", ".gdab__pill .gdab__chip-sub"].forEach((sel) => {
        const el = this.$(sel);
        if (el) el.dataset.tickText =
          [...el.childNodes].map((n) => n.textContent).join(" ").replace(/\s+/g, " ").trim();
      });
      this.setAttribute("data-gdab-state", "pill");
      this.setAttribute("data-gdab-content", "offer");
      this.setSurface(this.cfg.surfaceForce || this.getAttribute("data-gdab-surface") || "primary");
      this.bind();
      const routeCard = this.$(".gdab__card[data-default-selected]:not([data-unavailable])")
        || this.$(".gdab__card[data-route-selected]:not([data-unavailable])")
        || this.$(".gdab__card:not([data-unavailable])");
      if (routeCard) this.selectCard(routeCard, { silent: true, animate: false });
    }

    // ------------------------------------------------- external resources
    armExternal() {
      if (this.__armed) return;
      this.__armed = true;
      this._cleanup = [];
      const on = (target, ev, fn, opts) => {
        target.addEventListener(ev, fn, opts);
        this._cleanup.push(() => target.removeEventListener(ev, fn, opts));
      };
      on(window, "pagehide", () => {
        this.resetCheckout();
        this.cancelCartRefresh();
      });
      on(window, "pageshow", (event) => {
        if (event.persisted || this._checkoutAttempt?.navigating) this.resetCheckout();
        this.queueCartRefresh();
      });
      on(window, "focus", () => this.queueCartRefresh());
      on(document, "visibilitychange", () => {
        if (document.visibilityState === "visible") this.queueCartRefresh();
        else this.cancelCartRefresh();
      });
      on(document, "submit", (event) => {
        if (!event.target.closest("nex-hero-purchase")) return;
        this._cartEpoch = (this._cartEpoch || 0) + 1;
        this.cancelCartRefresh();
        this.queueCartRefresh();
      }, true);
      this._cleanup.push(() => {
        this.cancelCartRefresh();
        cancelAnimationFrame(this._cartFocusFrame);
        clearTimeout(this._cartFocusTimer);
        this._cartFocusFrame = 0;
        this._cartFocusTimer = 0;
      });
      this.queueCartRefresh();
      this.armBootEntrance();
      this.armShopPayGuard();
      this.setupReviewSizing();
      // header cart entry (site.js dispatches on icon tap)
      on(document, "gdab:open-cart", () => this.openCartDirect());
      // Other purchase surfaces keep their real Shopify product forms as a
      // no-JS fallback. When this upgraded drawer owns the same variant it
      // may accept the request and run the one hardened add/cart pipeline.
      on(document, "gdab:add-variant", (event) => {
        if (this.addExternalVariant(event.detail || {})) event.preventDefault();
      });
      // Keep the persistent purchase dock on the same available colour a
      // shopper selected in the Offer section on every route. The loaded
      // Product URL remains the document identity; colour choice is transient
      // purchase state. A sold-out Offer card deliberately leaves GDAB on an
      // available sibling.
      on(document, "offer:color-select", (event) => {
        const variantId = String(event.detail && event.detail.variantId || "");
        const card = [...this.querySelectorAll(".gdab__card")]
          .find((item) => item.dataset.variantId === variantId);
        if (!card || card.hasAttribute("data-unavailable")) return;
        this.selectCard(card, { silent: false });
      });
      // The inline hero purchase panel and the floating dock share the same
      // linked-product selection without duplicating checkout state.
      on(document, "gdab:select-variant", (event) => {
        const variantId = String(event.detail && event.detail.variantId || "");
        const card = [...this.querySelectorAll(".gdab__card")]
          .find((item) => item.dataset.variantId === variantId);
        if (!card || card.hasAttribute("data-unavailable")) return;
        this.selectCard(card, { silent: false, animate: true });
      });

      // input modality: the focus ring belongs to keyboard users only —
      // pointer opens focus the dialog container (invisible, still lands
      // AT users inside the modal per the ARIA pattern)
      this._kb = false;
      const modality = (kb) => {
        this._kb = kb;
        this.setAttribute("data-modality", kb ? "keyboard" : "pointer");
      };
      modality(false);
      on(window, "keydown", () => modality(true), { passive: true });
      on(window, "pointerdown", () => modality(false), { passive: true });
      on(window, "touchstart", () => modality(false), { passive: true });

      if (!this.cfg.surfaceForce) {
        this.refreshSurfaces();
        const surfaceScroll = () => this.queueSurfaceSync();
        on(window, "scroll", surfaceScroll, { passive: true });
        if (window.visualViewport) {
          on(window.visualViewport, "scroll", surfaceScroll, { passive: true });
          on(window.visualViewport, "resize", () => {
            if (this._closing) this.finishCloseToPill();
            surfaceScroll();
          }, { passive: true });
        }
        /* A background/foreground cycle may purge either the resolver or
           paint rAF on iOS. Re-enter with a clean gate and synchronous truth. */
        const recoverSurface = () => {
          if (document.visibilityState === "visible") this.recoverSurfaceSync();
        };
        on(document, "visibilitychange", recoverSurface);
        on(window, "pageshow", recoverSurface, { passive: true });
        this._cleanup.push(() => {
          if (this._surfaceFrame) cancelAnimationFrame(this._surfaceFrame);
          if (this._surfacePaintFrame) cancelAnimationFrame(this._surfacePaintFrame);
          if (this._surfacePaintFrame2) cancelAnimationFrame(this._surfacePaintFrame2);
          clearTimeout(this._surfaceFrameBelt);
          clearTimeout(this._surfacePaintBelt);
          this._surfaceFrame = 0;
          this._surfaceFrameAt = 0;
          this._surfaceFrameBelt = 0;
          this._surfacePaintFrame = 0;
          this._surfacePaintFrame2 = 0;
          this._surfacePaintAt = 0;
          this._surfacePaintBelt = 0;
          this.removeAttribute("data-gdab-surface-sync");
          this.removeAttribute("data-gdab-hero-hold");
        });
      }

      // theme editor re-renders neighbouring sections; the surface
      // markers may change (the rotation engine watches nothing else —
      // the old video/topic observers died with the behavioral triggers)
      on(document, "shopify:section:load", () => this.refreshSurfaces());
      on(document, "visibilitychange", () => {
        if (document.visibilityState === "hidden") this.stopReviewRotation();
        else if (this.state === "panel") this.startReviewRotation();
      });
      let rzT;
      on(window, "resize", () => {
        if (this._closing) this.finishCloseToPill();
        clearTimeout(rzT);
        rzT = setTimeout(() => {
          this.refreshSurfaces();
          this.positionPlate(this.$('.gdab__card[aria-checked="true"]'));
          if (this.state === "panel") this.startReviewRotation();
        }, 150);
      }, { passive: true });
      this._cleanup.push(() => clearTimeout(rzT));
    }
    disarmExternal() {
      this.resetCheckout();
      this.__armed = false;
      this.__rotationArmed = false;
      this._heroSurfaceHold = false;
      this._heroMorphState = "";
      this.removeAttribute("data-gdab-hero-hold");
      this.removeAttribute("data-hero-source-visible");
      this.clearReturnSettle();
      this.clearPanelCloseVisual();
      // theme-editor re-render mid-panel/cart must not strand the page
      // inert + scroll-locked (QA find)
      if (this.state !== "pill") {
        try { this.lockPage(false); } catch (e) { /* page over perfection */ }
        this.state = "pill";
      }
      // an unresolved optimistic add dies with the session: its gen-guarded
      // continuation self-discards, and nothing may resurrect the UI state
      this._pending = null;
      this._skeleton = false;
      this._skelFailed = false;
      if (this.cart) this.cart.classList.remove("is-updating", "gdab__cart--skel");
      clearTimeout(this._walletLingerT);   // timers disarm on disconnect
      this.removeAttribute("data-gdab-wallet-linger");
      (this._cleanup || []).forEach((fn) => fn());
      this._cleanup = [];
      [this.ambientT, this._coSheenT, this._returnT, this._sweepT, this._panelColorT,
        this._reviewT, this._reviewSwapT]
        .forEach(clearTimeout);
      if (this._reviewLayoutRaf) cancelAnimationFrame(this._reviewLayoutRaf);
      this._reviewLayoutRaf = 0;
      if (this._reviewResizeObserver) this._reviewResizeObserver.disconnect();
      this._reviewResizeObserver = null;
      if (this._reviewMeasure) this._reviewMeasure.remove();
      this._reviewMeasure = null;
      if (this._reviewAbort) this._reviewAbort.abort();
      this._reviewAbort = null;
      this._reviewLoadPromise = null;
      // the return-delay latch belt was just cleared with the timers — the
      // class itself must not survive a re-render either
      if (this.pill) this.pill.classList.remove("gdab__pill--return");
      if (this.surfaceIO) { this.surfaceIO.disconnect(); this.surfaceIO = null; }
    }

    // ------------------------------------------------------------ surface
    // Mobile uses one synchronous, rAF-throttled resolver at the RESTING
    // dock band. The old 1px IntersectionObserver was rebuilt while the form
    // was translated through the hero, freezing its band at the wrong y and
    // retaining stale colours whenever no marker intersected that pixel.
    queueSurfaceSync() {
      if (this.cfg.surfaceForce) return;
      const now = performance.now();
      /* WebKit can discard a pending rAF without clearing its numeric id.
         A later scroll is the heartbeat: retire any stale gate so surface
         resolution can never freeze permanently. */
      if (this._surfaceFrame &&
          now - (this._surfaceFrameAt || 0) > 400) {
        cancelAnimationFrame(this._surfaceFrame);
        clearTimeout(this._surfaceFrameBelt);
        this._surfaceFrame = 0;
        this._surfaceFrameAt = 0;
        this._surfaceFrameBelt = 0;
      }
      if (this.hasAttribute("data-gdab-surface-sync") &&
          now - (this._surfacePaintAt || 0) > 400) {
        if (this._surfacePaintFrame) cancelAnimationFrame(this._surfacePaintFrame);
        if (this._surfacePaintFrame2) cancelAnimationFrame(this._surfacePaintFrame2);
        clearTimeout(this._surfacePaintBelt);
        this._surfacePaintFrame = 0;
        this._surfacePaintFrame2 = 0;
        this._surfacePaintAt = 0;
        this._surfacePaintBelt = 0;
        this.removeAttribute("data-gdab-surface-sync");
      }
      if (this._surfaceFrame) return;
      this._surfaceFrameAt = now;
      clearTimeout(this._surfaceFrameBelt);
      this._surfaceFrameBelt = setTimeout(() => {
        this._surfaceFrameBelt = 0;
        if (!this.isConnected || !this._surfaceFrame) return;
        cancelAnimationFrame(this._surfaceFrame);
        this._surfaceFrame = 0;
        this._surfaceFrameAt = 0;
        this.syncSurfaceNow();
      }, 450);
      this._surfaceFrame = requestAnimationFrame(() => {
        clearTimeout(this._surfaceFrameBelt);
        this._surfaceFrameBelt = 0;
        this._surfaceFrame = 0;
        this._surfaceFrameAt = 0;
        this.syncSurfaceNow();
      });
    }
    recoverSurfaceSync() {
      if (this.cfg.surfaceForce || !this.isConnected) return;
      if (this._surfaceFrame) cancelAnimationFrame(this._surfaceFrame);
      if (this._surfacePaintFrame) cancelAnimationFrame(this._surfacePaintFrame);
      if (this._surfacePaintFrame2) cancelAnimationFrame(this._surfacePaintFrame2);
      clearTimeout(this._surfaceFrameBelt);
      clearTimeout(this._surfacePaintBelt);
      this._surfaceFrame = 0;
      this._surfaceFrameAt = 0;
      this._surfaceFrameBelt = 0;
      this._surfacePaintFrame = 0;
      this._surfacePaintFrame2 = 0;
      this._surfacePaintAt = 0;
      this._surfacePaintBelt = 0;
      this.removeAttribute("data-gdab-surface-sync");
      this.syncSurfaceNow();
    }
    restingPillCenterY() {
      /* Surface context belongs to the invariant resting dock band, never the
         temporarily tall/moving morph pill. Using the stage center keeps the
         white↔primary boundary exact through source, active, target, resize,
         and desktop observer setup. */
      const stage = this.$(".gdab__stage");
      const stageRect = stage ? stage.getBoundingClientRect() : null;
      const targetHeight =
        parseFloat(getComputedStyle(this).getPropertyValue("--gdab-height")) || 68;
      const measured = stageRect
        ? stageRect.top + targetHeight / 2
        : innerHeight - 58;
      const fallback = innerHeight - 58;
      return Math.max(0, Math.min(innerHeight - 1,
        Math.round(Number.isFinite(measured) ? measured : fallback)));
    }
    pickSurfaceMarker(centerY) {
      let winner = null;
      (this._surfaceMarkers || []).forEach((marker) => {
        const rect = marker.getBoundingClientRect();
        if (rect.top <= centerY && rect.bottom >= centerY) winner = marker;
      });
      return winner;
    }
    syncSurfaceNow() {
      if (!this.isConnected || !this.__armed || this.cfg.surfaceForce ||
          matchMedia("(min-width: 1024px)").matches) return;
      if (!this._surfaceMarkers) {
        this._surfaceMarkers =
          Array.from(document.querySelectorAll("[data-gdab-context]"));
      }
      const winner = this.pickSurfaceMarker(this.restingPillCenterY());
      const morphState = document.documentElement.dataset.heroMorphState || "";
      const heroOwns = morphState === "source" || morphState === "active";
      const heroBand = !!(winner && winner.closest(".hero"));
      const hold = heroOwns || heroBand;
      this.setHeroSurfaceHold(hold);
      /* Motion hold freezes copy/ambient rotation, but no longer freezes the
         material color. Once the resting dock band crosses out of the hero,
         its complete palette follows the new section in that same frame. */
      const heroMaterial =
        morphState === "source" || heroBand ||
        this.hasAttribute("data-hero-source-visible");
      this.setSurface(heroMaterial
        ? "light"
        : (winner ? winner.getAttribute("data-gdab-context") : "primary"));
    }
    refreshSurfaces() {
      if (!this.isConnected || !this.__armed || this.cfg.surfaceForce) return;
      if (this.surfaceIO) {
        this.surfaceIO.disconnect();
        this.surfaceIO = null;
      }
      this._surfaceMarkers =
        Array.from(document.querySelectorAll("[data-gdab-context]"));
      if (!this._surfaceMarkers.length) {
        this.setHeroSurfaceHold(false);
        this.setSurface("primary");
        return;
      }

      // Desktop never participates in the hero material handoff. Preserve its
      // original observer model and boot choreography exactly.
      if (!matchMedia("(min-width: 1024px)").matches) {
        this.syncSurfaceNow();
        return;
      }
      this.setHeroSurfaceHold(false);
      const centerY = this.restingPillCenterY();
      const initial = this.pickSurfaceMarker(centerY);
      this.setSurface(initial
        ? initial.getAttribute("data-gdab-context")
        : "primary");
      const markers = this._surfaceMarkers;
      const onBand = new Set();
      this.surfaceIO = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) onBand.add(entry.target);
          else onBand.delete(entry.target);
        }
        let winner = null;
        markers.forEach((marker) => {
          if (onBand.has(marker)) winner = marker;
        });
        this.setSurface(winner
          ? winner.getAttribute("data-gdab-context")
          : "primary");
      }, {
        rootMargin:
          `${-centerY}px 0px ${-(innerHeight - centerY - 1)}px 0px`,
        threshold: 0,
      });
      markers.forEach((marker) => this.surfaceIO.observe(marker));
    }
    setHeroSurfaceHold(hold) {
      const next = !!hold;
      if (this._heroSurfaceHold === next) return;
      this._heroSurfaceHold = next;
      this.toggleAttribute("data-gdab-hero-hold", next);
      if (next) {
        clearTimeout(this.ambientT);
        this.showOffer();
        this.pill.classList.remove(
          "gdab__pill--xfade", "gdab__pill--return", "gdab__pill--sheen");
      } else if (this.__rotationArmed && this.state === "pill") {
        this.scheduleAmbient();
      }
    }
    setHeroMorphState(state) {
      this._heroMorphState = state || "";
      /* The hero scrub replaces GDAB's own boot entrance. Release that boot
         gate on the exact dock-ready frame; otherwise the bar looks complete
         while its form remains inert until the later Highlights event. */
      if (state === "target" && typeof this._settleBootEntrance === "function") {
        this._settleBootEntrance();
      }
      if (matchMedia("(min-width: 1024px)").matches) {
        this.setHeroSurfaceHold(false);
        this.refreshSurfaces();
        return;
      }
      if (state === "source") {
        this.setHeroSurfaceHold(true);
        this.setSurface("light");
      } else {
        /* Active motion still freezes the offer rotation inside
           syncSurfaceNow(), while its palette is resolved from the live dock
           band. This keeps boundary color and geometry on one frame. */
        this.syncSurfaceNow();
      }
    }
    setSurface(surface) {
      if (!surface || this.getAttribute("data-gdab-surface") === surface) return;
      // Palette changes are one atomic frame. Independent 200/300ms colour
      // transitions produced pink shells with already-dark copy on reversals.
      // A surface is a complete material state, never a collection of races.
      this.setAttribute("data-gdab-surface-sync", "");
      this._surfacePaintAt = performance.now();
      if (this.__init) this.clearSweep();
      this.pill.classList.remove("gdab__pill--xfade");
      this.setAttribute("data-gdab-surface", surface);
      if (this._surfacePaintFrame) cancelAnimationFrame(this._surfacePaintFrame);
      if (this._surfacePaintFrame2) cancelAnimationFrame(this._surfacePaintFrame2);
      clearTimeout(this._surfacePaintBelt);
      this._surfacePaintBelt = setTimeout(() => {
        this._surfacePaintBelt = 0;
        if (!this.isConnected ||
            !this.hasAttribute("data-gdab-surface-sync")) return;
        if (this._surfacePaintFrame) cancelAnimationFrame(this._surfacePaintFrame);
        if (this._surfacePaintFrame2) cancelAnimationFrame(this._surfacePaintFrame2);
        this._surfacePaintFrame = 0;
        this._surfacePaintFrame2 = 0;
        this._surfacePaintAt = 0;
        this.removeAttribute("data-gdab-surface-sync");
      }, 450);
      this._surfacePaintFrame = requestAnimationFrame(() => {
        this._surfacePaintFrame = 0;
        this._surfacePaintFrame2 = requestAnimationFrame(() => {
          clearTimeout(this._surfacePaintBelt);
          this._surfacePaintBelt = 0;
          this._surfacePaintFrame2 = 0;
          this._surfacePaintAt = 0;
          this.removeAttribute("data-gdab-surface-sync");
        });
      });
    }
    reducedMotion() {
      return matchMedia("(prefers-reduced-motion: reduce)").matches;
    }

    // --------------------------------------------- first-load entrance
    // CSS owns the frames; JS owns the transient state around them. The
    // actual form and Nexie stay inert until BOTH tracks settle, then .boot
    // is removed so clip-path/animation declarations cannot become lasting
    // paint or compositor costs. The belt spans the layout's 1200ms boot
    // fallback plus this 1000ms animation for slow/lost site.js cases.
    armBootEntrance() {
      const root = document.documentElement;
      const publishComplete = () => {
        if (this.isConnected && ACTIVE === this) {
          root.setAttribute("data-gdab-boot-complete", "");
          if (!this.__rotationArmed) {
            this.__rotationArmed = true;
            this.armRotation();
          }
        }
      };

      root.removeAttribute("data-gdab-boot-complete");
      if (!this.classList.contains("boot") || !this.form || !this.nexie) {
        publishComplete();
        return;
      }

      this.setAttribute("data-gdab-booting", "");
      const gated = [this.form, this.nexie].map((el) => ({
        el,
        inert: el.hasAttribute("inert"),
      }));
      const focusGates = [this.pill, this.nexie].map((el) => ({
        el,
        hadTabindex: el.hasAttribute("tabindex"),
        tabindex: el.getAttribute("tabindex"),
      }));
      const hints = [
        { el: this.form, value: this.form.style.getPropertyValue("will-change"),
          priority: this.form.style.getPropertyPriority("will-change") },
        { el: this.nexie, value: this.nexie.style.getPropertyValue("will-change"),
          priority: this.nexie.style.getPropertyPriority("will-change") },
      ];
      gated.forEach(({ el }) => el.setAttribute("inert", ""));
      focusGates.forEach(({ el }) => el.setAttribute("tabindex", "-1"));
      this.form.style.setProperty("will-change", "transform, clip-path, opacity");
      this.nexie.style.setProperty("will-change", "transform, opacity");

      let settled = false;
      let belt = 0;
      let unlisten = () => {};
      const settle = () => {
        if (settled) return;
        settled = true;
        if (this._settleBootEntrance === settle) {
          this._settleBootEntrance = null;
        }
        clearTimeout(belt);
        unlisten();
        this.classList.remove("boot");
        this.removeAttribute("data-gdab-booting");
        hints.forEach(({ el, value, priority }) => {
          if (value) el.style.setProperty("will-change", value, priority);
          else el.style.removeProperty("will-change");
        });
        gated.forEach(({ el, inert }) => {
          if (!inert) el.removeAttribute("inert");
        });
        focusGates.forEach(({ el, hadTabindex, tabindex }) => {
          if (hadTabindex) el.setAttribute("tabindex", tabindex);
          else el.removeAttribute("tabindex");
        });
        publishComplete();
      };
      this._settleBootEntrance = settle;

      const runEntrance = () => {
        if (settled) return;
        const heroOwnsEntrance =
          matchMedia("(max-width: 1023.98px)").matches &&
          document.querySelector(".hero .hero-purchase");
        if (this.reducedMotion() || heroOwnsEntrance ||
            this.hasAttribute("data-hero-morph-target")) {
          settle();
          return;
        }

        const ended = new Set();
        const onEnd = (event) => {
          if (event.target === this.form && event.animationName === "gdab-apple-pill-in") {
            ended.add(this.form);
          } else if (event.target === this.nexie && event.animationName === "gdab-apple-nexie-in") {
            ended.add(this.nexie);
          } else {
            return;
          }
          if (ended.size === 2) settle();
        };
        const events = ["animationend", "animationcancel"];
        events.forEach((event) => {
          this.form.addEventListener(event, onEnd);
          this.nexie.addEventListener(event, onEnd);
        });
        unlisten = () => events.forEach((event) => {
          this.form.removeEventListener(event, onEnd);
          this.nexie.removeEventListener(event, onEnd);
        });

        const waitingForBoot = root.getAttribute("data-boot") === "pending";
        if (!waitingForBoot) requestAnimationFrame(() => {
          if (settled || typeof this.form.getAnimations !== "function") return;
          const tracks = [...this.form.getAnimations(), ...this.nexie.getAnimations()];
          if (!tracks.length || tracks.every((animation) => animation.playState === "finished")) settle();
        });
        belt = setTimeout(settle, waitingForBoot ? 2400 : 1200);
        this._cleanup.push(settle);
      };

      if (root.getAttribute("data-nex-widget-gate") === "waiting") {
        let released = false;
        const release = () => {
          if (released || !this.isConnected || ACTIVE !== this) return;
          released = true;
          document.removeEventListener("nex:highlights-ready", release);
          this.refreshSurfaces();
          runEntrance();
        };
        document.addEventListener("nex:highlights-ready", release);
        this._cleanup.push(() => {
          document.removeEventListener("nex:highlights-ready", release);
          if (!released) settle();
        });
        return;
      }

      runEntrance();
    }

    // ---------------- exact Shop Pay availability (selected color only)
    armShopPayGuard() {
      if (!this.confirmRow || !this.buyShell) return;
      this.setShopPayState("pending");
      const observer = new MutationObserver(() => this.queueShopPayCheck());
      observer.observe(this.buyShell, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ["recommended", "class", "hidden", "style"],
      });
      this._cleanup.push(() => observer.disconnect());

      [0, 120, 420, 900, 1600].forEach((delay) => {
        const timer = setTimeout(() => this.queueShopPayCheck(), delay);
        this._cleanup.push(() => clearTimeout(timer));
      });
      this._cleanup.push(() => {
        if (this._shopPayRaf) cancelAnimationFrame(this._shopPayRaf);
        this._shopPayRaf = 0;
      });
    }

    queueShopPayCheck() {
      if (this._shopPayRaf) return;
      this._shopPayRaf = requestAnimationFrame(() => {
        this._shopPayRaf = 0;
        this.refreshShopPayState();
      });
    }

    refreshShopPayState() {
      if (!this.confirmRow || !this.buyShell) return;
      const active = [...this.querySelectorAll(".gdab__buy-form")]
        .find((form) => !form.hidden);
      if (!active) {
        this.setShopPayState("unavailable");
        return;
      }

      const unbranded = active.querySelector(".shopify-payment-button__button--unbranded");
      const accelerated = active.querySelector("shopify-accelerated-checkout");
      const wallet = active.querySelector("shop-pay-wallet-button");
      let recommendation = null;
      if (accelerated) {
        try {
          recommendation = JSON.parse(accelerated.getAttribute("recommended") || "null");
        } catch (_) {
          recommendation = null;
        }
      }

      if (unbranded || (recommendation && recommendation.name !== "shop_pay")) {
        this.setShopPayState("unavailable");
      } else if (recommendation && recommendation.name === "shop_pay" && wallet) {
        this.setShopPayState("available");
      } else {
        this.setShopPayState("pending");
      }
    }

    setShopPayState(state) {
      if (!this.confirmRow || !this.buyShell) return;
      const available = state === "available";
      this.confirmRow.dataset.shopPay = state;
      this.buyShell.setAttribute("aria-hidden", available ? "false" : "true");
      this.buyShell.inert = !available || Boolean(window.NexDock?.busy || window.NexDock?.preview);
    }

    // ---------------------------------------------- persistent offer glow
    // Price and offer stay visible at every beat. The existing pill-wide
    // sheen keeps the exact merchant-configured cadence without replacing
    // the money information with rotating trust copy.
    armRotation() {
      this.cycleIdx = 0;
      this.scheduleAmbient();
    }
    scheduleAmbient() {
      clearTimeout(this.ambientT);
      if (this._heroSurfaceHold) return;
      this.ambientT = setTimeout(() => this.rotateStep(), this.cfg.ambientMs);
    }
    // One attention beat — also the QA harness's manual test hook.
    rotateStep() {
      if (this.state !== "pill" || this._heroSurfaceHold) return;
      this.content = "offer";
      this.setAttribute("data-gdab-content", "offer");
      this.sheenBeat();
      this.emit("gdab:dynamic-text",
        { reason: "glow", primary: "offer" });
      this.scheduleAmbient();
    }
    clearSweep() {
      clearTimeout(this._sweepT);
      if (this.trustEl) {
        this.trustEl.classList.remove("gdab__trust--sweep", "gdab__trust--swap");
      }
      this.pill.classList.remove("gdab__pill--sheen");
    }
    sweep(isSwap, withSheen) {
      if (this.reducedMotion()) return;
      this.clearSweep();
      if (!this.trustEl) return;
      void this.trustEl.offsetWidth;           // restart cleanly
      this.trustEl.classList.add(isSwap ? "gdab__trust--swap" : "gdab__trust--sweep");
      // the pill-wide sheen rides every message change (Jackson, Aug 10 —
      // superseding the earlier two-tier salience rule)
      if (withSheen) this.pill.classList.add("gdab__pill--sheen");
      const h = (e) => {
        if (e.animationName !== "gdab-sweep") return;
        this.trustEl.removeEventListener("animationend", h);
        this.clearSweep();                     // rest state == first keyframe
      };
      this.trustEl.addEventListener("animationend", h);
      // belt: animationend can stall in a suspended tab (house pattern) —
      // rest state == first keyframe, so a late clear can never pop
      this._sweepT = setTimeout(() => {
        this.trustEl.removeEventListener("animationend", h);
        this.clearSweep();
      }, 2400);
    }
    // the price slot's glow: sheen only. No glyph sweep runs on the offer
    // layer, so there is no trustEl animationend to hear — the timeout
    // belt IS the clear path here.
    sheenBeat() {
      if (this.reducedMotion()) return;
      this.clearSweep();
      void this.pill.offsetWidth;
      this.pill.classList.add("gdab__pill--sheen");
      this._sweepT = setTimeout(() => this.clearSweep(), 2400);
    }
    // Display-only offer flip for the panel-open seam (the confirm row
    // mirrors offer content, so the close reveal must land on prices).
    // The cycle INDEX is untouched: rotation resumes exactly where it
    // left off when the sheet closes (Jackson, Aug 12).
    showOffer() {
      clearTimeout(this.ambientT);
      this.content = "offer";
      this.setAttribute("data-gdab-content", "offer");
      this.clearSweep();
    }

    // ----------------------------------------- expanded-panel review proof
    normalizeReviews(reviews) {
      const seen = new Set();
      const productGroup = String(this.reviewProof?.dataset.reviewProductGroup || "")
        .trim().toLocaleLowerCase();
      return (Array.isArray(reviews) ? reviews : []).reduce((eligible, review) => {
        const body = String(review?.body || "").replace(/\s+/g, " ").trim();
        const group = String(review?.productGroupKey || "").trim().toLocaleLowerCase();
        const timestamp = Date.parse(review?.publishedAt || review?.createdAt || "");
        if (!body || Number(review?.rating) !== 5 || !productGroup ||
            group !== productGroup || review?.metadata?.is_store_review === true ||
            !Number.isFinite(timestamp)) return eligible;
        const key = String(review.id || body).toLocaleLowerCase();
        if (seen.has(key)) return eligible;
        seen.add(key);
        eligible.push({ ...review, body, timestamp });
        return eligible;
      }, []).sort((a, b) => b.timestamp - a.timestamp).slice(0, 5);
    }

    setupReviewSizing() {
      if (!this.reviewCopy || this._reviewResizeObserver) return;
      const queue = () => {
        if (!this.isConnected || !this.__armed) return;
        const styles = getComputedStyle(this.reviewCopy);
        const signature = `${this.reviewCopy.clientWidth}|${styles.font}|${styles.letterSpacing}`;
        if (signature === this._reviewMeasuredSignature) return;
        this._reviewMeasuredSignature = signature;
        if (this._reviewLayoutRaf) cancelAnimationFrame(this._reviewLayoutRaf);
        this._reviewLayoutRaf = requestAnimationFrame(() => {
          this._reviewLayoutRaf = 0;
          this.syncReviewOverflow();
          window.NexDock?.layout?.schedule();
        });
      };
      if (typeof ResizeObserver === "function") {
        this._reviewResizeObserver = new ResizeObserver(queue);
        this._reviewResizeObserver.observe(this.reviewCopy);
      }
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
          this._reviewMeasuredSignature = "";
          queue();
        });
      }
    }

    syncReviewOverflow() {
      if (!this.reviewCopy || !this.reviewText) return;
      const overflowing = this.reviewText.clientWidth > 0 &&
        this.reviewText.scrollWidth > this.reviewText.clientWidth + 1;
      this.reviewCopy.toggleAttribute("data-review-overflow", overflowing);
    }

    reviewPool() {
      return this.reviews || [];
    }

    loadReviews() {
      if (!this.reviewApi || this._reviewLoadPromise) return this._reviewLoadPromise;
      const controller = new AbortController();
      this._reviewAbort = controller;
      this._reviewLoadPromise = (async () => {
        const reviews = [];
        let cursor = "";
        let pages = 0;
        do {
          const url = new URL(this.reviewApi, window.location.origin);
          if (cursor) url.searchParams.set("cursor", cursor);
          const response = await fetch(url.toString(), {
            credentials: "omit",
            signal: controller.signal,
            headers: { Accept: "application/json" },
          });
          if (!response.ok) throw new Error(`Reviews request failed (${response.status})`);
          const payload = await response.json();
          if (Array.isArray(payload.reviews)) reviews.push(...payload.reviews);
          cursor = String(payload.nextCursor || "");
          pages += 1;
        } while (cursor && this.normalizeReviews(reviews).length < 5 && pages < 20);
        this.reviews = this.normalizeReviews(reviews);
        this.reviewIndex = 0;
        if (this.state === "panel") this.startReviewRotation();
      })().catch((error) => {
        if (error.name !== "AbortError") this.emit("gdab:reviews-error");
      }).finally(() => {
        if (this._reviewAbort === controller) this._reviewAbort = null;
      });
      return this._reviewLoadPromise;
    }

    reviewLabel(review) {
      const body = String(review && review.body || "").trim();
      const name = String(review && review.reviewerName || "").trim();
      return body ? `“${body}”${name ? ` — ${name}` : ""}` : "";
    }

    startReviewRotation() {
      this.stopReviewRotation();
      if (!this.reviewCopy) return;
      const reviews = this.reviewPool();
      this.reviewCopy.hidden = !reviews.length;
      if (!reviews.length) {
        this.reviewText.textContent = "";
        this.syncReviewOverflow();
        return;
      }
      const show = () => {
        const review = reviews[this.reviewIndex % reviews.length];
        this.reviewText.textContent = this.reviewLabel(review);
        this.reviewCopy.dataset.reviewId = String(review.id || "");
        this.syncReviewOverflow();
        this.reviewIndex = (this.reviewIndex + 1) % reviews.length;
      };
      show();
      if (
        reviews.length < 2 ||
        document.visibilityState === "hidden" ||
        this.reducedMotion()
      ) return;
      const next = () => {
        if (
          this.state !== "panel" ||
          document.visibilityState === "hidden" ||
          this.reducedMotion()
        ) return;
        this.reviewCopy.classList.add("is-changing");
        this._reviewSwapT = setTimeout(() => {
          show();
          this.reviewCopy.classList.remove("is-changing");
        }, 150);
        this._reviewT = setTimeout(next, 5000);
      };
      this._reviewT = setTimeout(next, 5000);
    }

    stopReviewRotation() {
      clearTimeout(this._reviewT);
      clearTimeout(this._reviewSwapT);
      this._reviewT = 0;
      this._reviewSwapT = 0;
      if (this.reviewCopy) this.reviewCopy.classList.remove("is-changing");
    }

    // ---------------------------------------------------------------- bind
    bind() {
      // the pill is a real submit button in a real product form; enhancement
      // intercepts it, absence of JS submits natively
      this.pill.addEventListener("click", (e) => {
        e.preventDefault();
        this.openPanel();
      });
      this.$(".gdab__panel-close").addEventListener("click", () => this.closeToPill());
      this.scrim.addEventListener("click", () => this.closeToPill());
      this.$(".gdab__cart-close").addEventListener("click", () => this.closeToPill());
      // vertical qty stepper (Figma: + / chip / −). Delegated; − at 1
      // removes the line. One in-flight change at a time, and everything
      // parks while an optimistic add is unresolved (a change response
      // racing the add would double-render the incoming line).
      this.cart.addEventListener("click", (e) => {
        const retry = e.target.closest(".gdab__line-retry");
        if (retry) {
          // ONE retry control, two failure domains: a dead header-open
          // refresh re-fetches /cart.js; a dead add first reconciles cart
          // truth and re-POSTs only when the original definitely did not
          // land; a quantity failure repeats only that line's last request.
          if (this._skelFailed) this.retryRefresh();
          else if (this._changeRetry) this.retryChange();
          else this.retryAdd();
          return;
        }
        const btn = e.target.closest(".gdab__step");
        if (!btn) return;
        const line = btn.closest(".gdab__cart-line");
        if (!line || this._changing) return;
        if (this._pending && !this._pending.failed) return;
        const qty = parseInt(line.dataset.qty, 10) || 1;
        const next = btn.classList.contains("gdab__step--plus") ? qty + 1 : qty - 1;
        this.changeLine(line, next);
      });
      this.querySelectorAll(".gdab__card").forEach((card) =>
        card.addEventListener("click", () => this.selectCard(card, { animate: true })));
      if (this.checkoutForm) {
        this.checkoutForm.addEventListener("submit", (event) => this.confirm(event));
      } else {
        this.confirmBtn.addEventListener("click", (event) => this.confirm(event));
      }
      // the gray Buy-now placeholder is a dead control until a colour is
      // chosen — dead taps must speak (same rule as the confirm button)
      const buyPh = this.$(".gdab__buy-placeholder");
      if (buyPh) buyPh.addEventListener("click", () => {
        this.announce(this.t("pickColor"));
      });
      this.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && this.state !== "pill") {
          e.stopPropagation();
          this.closeToPill();
        }
        if (e.key === "Tab" && this.state !== "pill") this.trapFocus(e);
      });
    }
    trapFocus(e) {
      const zone = this.state === "panel" ? this.panel : this.cart;
      const items = [...zone.querySelectorAll(FOCUSABLE)].filter(isVisible);
      if (!items.length) {
        e.preventDefault();
        return;
      }
      const first = items[0], last = items[items.length - 1];
      // the accelerated-checkout wallets render in a CLOSED shadow root
      // (or an iframe), so they can never appear in items — yet they ARE
      // genuinely tabbable and sit after every matched item in DOM order.
      // Without this, the wrap on `last` made the Buy-now button
      // unreachable by keyboard (review find). document.activeElement
      // reports the shadow HOST / iframe element, so containment works.
      let wallet = this.state === "panel"
        ? this.$(".gdab__buy-form:not([hidden])")
        : this.$(".gdab__cart-express:not(.is-disabled)");
      // a hidden block (empty cart, skeleton) must not open an escape
      // hatch in the trap
      if (wallet && !isVisible(wallet)) wallet = null;
      if (wallet && wallet.contains(document.activeElement)) {
        // Tab out of the wallet wraps to the zone's first control;
        // shift+Tab walks back naturally into the matched items
        if (!e.shiftKey) { first.focus(); e.preventDefault(); }
        return;
      }
      if (!zone.contains(document.activeElement) || document.activeElement === zone) {
        (e.shiftKey ? last : first).focus();
        e.preventDefault();
      }
      else if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault(); }
      else if (!e.shiftKey && document.activeElement === last) {
        // a live wallet after the last matched item is the REAL last
        // stop: let Tab flow into it naturally
        if (wallet) return;
        first.focus(); e.preventDefault();
      }
    }

    // --------------------------------------------------------------- panel
    lockPage(lock) {
      // inert (aria-modal alone is not honored consistently by VO in
      // WKWebView) + scroll lock. Walks UP from the component: at every
      // ancestor level, all siblings off the component's branch are
      // inerted — a body-children-only walk exempted the entire <main>
      // subtree in a real Shopify layout (QA find). The locked set is
      // remembered so unlock restores exactly what lock touched.
      // ZERO-LAYOUT scroll lock: overflow/position locks either clamp
      // scrollY to 0 (rewinding the scroll stories) or unstick every
      // pinned sticky stage (the page jumped). Blocking the gestures
      // touches no layout at all; the sheet keeps its own scrolling.
      if (!this._gestureBlock) {
        this._gestureBlock = (e) => {
          if (e.target && e.target.closest && e.target.closest(".gdab__cart-scroll, .gdab__cart .gdab__cart-foot, .gdab__panel")) return;
          e.preventDefault();
        };
      }
      if (!this._keyBlock) {
        // desktop gap: Space/PageDown/arrows scrolled the document behind
        // the open panel/cart (only touch+wheel were blocked)
        const NAV = [" ", "PageUp", "PageDown", "ArrowUp", "ArrowDown", "Home", "End"];
        this._keyBlock = (e) => {
          if (NAV.indexOf(e.key) === -1) return;
          const t = e.target;
          if (t && t.closest && t.closest(".gdab__cart-scroll, .gdab__cart .gdab__cart-foot, .gdab__panel")) return;
          if (t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return;
          e.preventDefault();
        };
      }
      document.documentElement.classList.toggle("gdab-scroll-lock", lock);
      if (lock) {
        document.addEventListener("touchmove", this._gestureBlock, { passive: false });
        document.addEventListener("wheel", this._gestureBlock, { passive: false });
        document.addEventListener("keydown", this._keyBlock, true);
      } else {
        document.removeEventListener("touchmove", this._gestureBlock, { passive: false });
        document.removeEventListener("wheel", this._gestureBlock, { passive: false });
        document.removeEventListener("keydown", this._keyBlock, true);
      }
      if (lock) {
        this._inertEls = [];
        let node = this;
        while (node.parentElement) {
          const parent = node.parentElement;
          [...parent.children].forEach((el) => {
            if (el === node || /^(SCRIPT|STYLE|LINK)$/.test(el.tagName)) return;
            const hasInert = "inert" in el;
            this._inertEls.push({
              el,
              hasInert,
              inert: hasInert ? el.inert : null,
              ariaHidden: el.getAttribute("aria-hidden"),
            });
            if (hasInert) el.inert = true;
            else el.setAttribute("aria-hidden", "true");
          });
          if (parent === document.body) break;
          node = parent;
        }
      } else {
        (this._inertEls || []).forEach((entry) => {
          const { el, hasInert, inert, ariaHidden } = entry;
          if (hasInert) el.inert = inert;
          else if (ariaHidden === null) el.removeAttribute("aria-hidden");
          else el.setAttribute("aria-hidden", ariaHidden);
        });
        this._inertEls = [];
      }
      // the surface engine is pointless while the pill is hidden
      if (lock) { if (this.surfaceIO) this.surfaceIO.disconnect(); }
      else this.refreshSurfaces();
    }

    openPanel() {
      if (this.state !== "pill") return;
      if (this.getAttribute("data-gdab-sell") === "soldout") {
        this.announce(this.t("soldOut"));   // dead taps must speak
        return;
      }
      this.clearReturnSettle();
      this.clearPanelCloseVisual();
      if ("inert" in this.panel) this.panel.inert = false;
      this.lastFocus = document.activeElement;
      this.lockPage(true);
      this.state = "panel";
      this.startReviewRotation();
      this.loadReviews();
      this.pill.classList.remove("gdab__pill--return");  // open is never delayed
      // (the chip-width copy onto the Confirm button is gone — the
      // two-button row sizes itself by flex, Jackson Aug 26; the chip⇄
      // confirm morph is color-only and survives the geometry change)
      this.setAttribute("data-gdab-state", "panel");
      // colour-card photos load on first open, not at page load — the
      // fixed-position GDAB defeats loading="lazy" (always in-viewport),
      // so the cards use the same data-src hydration as the cart's
      // included strip. Attr-based: editor re-renders re-hydrate.
      this.panel.querySelectorAll("img[data-src]").forEach((im) => {
        im.src = im.dataset.src;
        im.removeAttribute("data-src");
      });
      this.setPillExposed(false);
      this.pill.setAttribute("aria-expanded", "true");
      this.showOffer();
      if (this._kb) {
        const firstCard = this.$(".gdab__card");
        (firstCard || this.$(".gdab__panel-close")).focus();
      } else {
        this.panel.focus();
      }
      this.announce(this.t("panelOpened"));
      this.emit("gdab:panel-open");
    }
    // Cart wallets need a short post-state linger because the cart closes
    // immediately. The purchase panel keeps its real panel state throughout
    // the longer visual return, so its wallets remain laid out naturally
    // until the handoff has actually finished.
    lingerWallets(duration = 340) {
      this.setAttribute("data-gdab-wallet-linger", "");
      clearTimeout(this._walletLingerT);
      this._walletLingerT = setTimeout(
        () => this.removeAttribute("data-gdab-wallet-linger"), duration);
    }

    // Clone the final visible value of any rolling label. A color switch can
    // still have old/new ticker spans in flight when close is requested; the
    // return layer must never carry both values into the morph.
    stableReturnClone(source) {
      const clone = source.cloneNode(true);
      clone.removeAttribute("id");
      clone.querySelectorAll("[id]").forEach((node) => node.removeAttribute("id"));
      clone.classList.remove("gdab__chip--morph", "gdab__pill--return");
      clone.querySelectorAll(".gdab__tick").forEach((ticker) => {
        const current = ticker.querySelector(".gdab__tick-in")
          || [...ticker.children].find((child) =>
            !child.classList.contains("gdab__tick-out"))
          || ticker.lastElementChild;
        const value = String(current ? current.textContent : ticker.textContent).trim();
        ticker.replaceChildren(document.createTextNode(value));
        ticker.classList.remove("gdab__tick", "gdab__tick-in", "gdab__tick-out");
        ticker.style.removeProperty("width");
      });
      clone.querySelectorAll(".gdab__tick-in, .gdab__tick-out").forEach((node) => {
        node.classList.remove("gdab__tick-in", "gdab__tick-out");
      });
      return clone;
    }

    buildPanelReturnVisual() {
      const chip = this.$(".gdab__pill .gdab__chip");
      const offer = this.$(".gdab__pill .gdab__offer");
      if (!chip || !offer || !this.confirmBtn || !this.panel) return null;

      const panelRect = this.panel.getBoundingClientRect();
      const from = this.confirmBtn.getBoundingClientRect();
      const to = chip.getBoundingClientRect();
      const offerRect = offer.getBoundingClientRect();
      if (!panelRect.width || !from.width || !to.width || !offerRect.width) return null;

      const visual = document.createElement("div");
      visual.className = "gdab__return-visual";
      visual.setAttribute("aria-hidden", "true");

      const offerClone = this.stableReturnClone(offer);
      offerClone.classList.add("gdab__return-offer");

      const cta = document.createElement("span");
      cta.className = "gdab__return-cta";
      const checkoutCopy = document.createElement("span");
      checkoutCopy.className = "gdab__return-checkout";
      checkoutCopy.textContent = String(this.confirmBtn.textContent || "").trim();

      const chipClone = this.stableReturnClone(chip);
      chipClone.classList.add("gdab__return-chip");
      cta.append(checkoutCopy, chipClone);
      visual.append(offerClone, cta);

      const px = (value) => Math.max(0, value).toFixed(3) + "px";
      visual.style.setProperty("--gdab-return-from-left", px(from.left - panelRect.left));
      visual.style.setProperty("--gdab-return-from-top", px(from.top - panelRect.top));
      visual.style.setProperty("--gdab-return-from-width", px(from.width));
      visual.style.setProperty("--gdab-return-from-height", px(from.height));
      visual.style.setProperty("--gdab-return-to-left", px(to.left - panelRect.left));
      visual.style.setProperty("--gdab-return-to-top", px(to.top - panelRect.top));
      visual.style.setProperty("--gdab-return-to-width", px(to.width));
      visual.style.setProperty("--gdab-return-to-height", px(to.height));
      visual.style.setProperty("--gdab-return-offer-left",
        px(offerRect.left - panelRect.left));
      visual.style.setProperty("--gdab-return-offer-top",
        px(offerRect.top - panelRect.top));
      visual.style.setProperty("--gdab-return-offer-width", px(offerRect.width));
      visual.style.setProperty("--gdab-return-offer-height", px(offerRect.height));
      visual.style.setProperty("--gdab-return-from-bg",
        getComputedStyle(this.confirmBtn).backgroundColor);
      visual.style.setProperty("--gdab-return-to-bg",
        getComputedStyle(chip).backgroundColor);
      this.panel.append(visual);
      return { visual, cta };
    }

    clearReturnSettle() {
      if (this._closeSettleFrame) cancelAnimationFrame(this._closeSettleFrame);
      if (this._closeSettleFrame2) cancelAnimationFrame(this._closeSettleFrame2);
      this._closeSettleFrame = 0;
      this._closeSettleFrame2 = 0;
      this.removeAttribute("data-gdab-return-settle");
    }

    clearPanelCloseVisual() {
      clearTimeout(this._closeT);
      this._closeT = 0;
      if (this._closeCta && this._closeEndHandler) {
        this._closeCta.removeEventListener("animationend", this._closeEndHandler);
      }
      if (this.panel && this._closePanelEndHandler) {
        this.panel.removeEventListener("transitionend", this._closePanelEndHandler);
      }
      if (this._closeFinishFrame) cancelAnimationFrame(this._closeFinishFrame);
      if (this._closeFinishFrame2) cancelAnimationFrame(this._closeFinishFrame2);
      this._closeFinishFrame = 0;
      this._closeFinishFrame2 = 0;
      this._closeAnimationDone = false;
      this._closeClipDone = false;
      if (this._closeVisual) this._closeVisual.remove();
      this._closeVisual = null;
      this._closeCta = null;
      this._closeEndHandler = null;
      this._closePanelEndHandler = null;
      this.removeAttribute("data-gdab-closing");
      if (this.panel) {
        this.panel.removeAttribute("aria-busy");
        if ("inert" in this.panel) this.panel.inert = false;
      }
      this._closing = false;
    }

    startPanelClose() {
      const built = this.buildPanelReturnVisual();
      if (!built) {
        this.finishCloseToPill();
        return;
      }
      this._closing = true;
      this._closeVisual = built.visual;
      this._closeCta = built.cta;
      if ("inert" in this.panel) this.panel.inert = true;
      this.panel.setAttribute("aria-busy", "true");

      // Commit the measured start frame before the closing hook turns on the
      // single synchronized CSS timeline.
      void built.visual.offsetWidth;
      this.setAttribute("data-gdab-closing", "");

      const finish = () => {
        if (this._closing) this.finishCloseToPill();
      };
      const finishAfterPaint = () => {
        if (!this._closing || !this._closeAnimationDone ||
            !this._closeClipDone || this._closeFinishFrame ||
            this._closeFinishFrame2) return;

        // animationend/transitionend run before the browser is required to
        // paint their final frame. Hold the inert return layer through the
        // next paint so a throttled iPhone cannot expose a geometric snap.
        this._closeFinishFrame = requestAnimationFrame(() => {
          this._closeFinishFrame = 0;
          finish();
        });
      };
      this._closeEndHandler = (event) => {
        if (event.target !== built.cta ||
            event.animationName !== "gdab-return-cta") return;
        this._closeAnimationDone = true;
        finishAfterPaint();
      };
      this._closePanelEndHandler = (event) => {
        if (event.target !== this.panel ||
            (event.propertyName !== "clip-path" &&
             event.propertyName !== "-webkit-clip-path")) return;
        this._closeClipDone = true;
        finishAfterPaint();
      };
      built.cta.addEventListener("animationend", this._closeEndHandler);
      this.panel.addEventListener("transitionend", this._closePanelEndHandler);

      // Safety belt only: normal completion is tied to both CSS endpoints
      // and two requestAnimationFrame paints, never this wall clock.
      this._closeT = setTimeout(finish, 650);
    }

    finishCloseToPill() {
      if (this.state === "pill") {
        this.clearPanelCloseVisual();
        return;
      }
      const animatedPanelClose = this._closing;
      if (animatedPanelClose) this.setAttribute("data-gdab-return-settle", "");
      this.clearPanelCloseVisual();
      this.lockPage(false);
      this.state = "pill";
      this.setAttribute("data-gdab-state", "pill");
      this.setPillExposed(true);
      this.pill.setAttribute("aria-expanded", "false");
      const lf = this.lastFocus;
      const focusSafely = (node) => {
        if (!node || typeof node.focus !== "function") return;
        try { node.focus({ preventScroll: true }); }
        catch (error) { node.focus(); }
      };
      if (lf && lf.isConnected && lf !== document.body &&
          lf !== document.documentElement &&
          lf.closest("nex-gdab") !== this) focusSafely(lf);
      else focusSafely(this.pill);

      // Return ownership to the mobile hero morph after an externally
      // opened drawer closes. At the dock endpoint the pill stays live;
      // in source/active phases it resumes the authored inert gate.
      if (this.hasAttribute("data-hero-morph-target") &&
          !this.hasAttribute("data-hero-morph-ready")) {
        this.inert = true;
        this.setAttribute("aria-hidden", "true");
      }
      this.emit("gdab:close");
      this.scheduleAmbient();

      // The inert return layer is pixel-identical to the final pill. Disable
      // the pill's ordinary surface/opacity transitions for the same paint in
      // which the layer is removed, then release that atomic handoff after two
      // frames. Without this, the real pill briefly faded up from zero after
      // the visual return had already finished.
      if (animatedPanelClose) {
        this._closeSettleFrame = requestAnimationFrame(() => {
          this._closeSettleFrame = 0;
          this._closeSettleFrame2 = requestAnimationFrame(() => {
            this._closeSettleFrame2 = 0;
            this.removeAttribute("data-gdab-return-settle");
          });
        });
      }
    }

    closeToPill() {
      if (this._closing || this.state === "pill") return;
      this.stopReviewRotation();
      const wasPanel = this.state === "panel";
      const wasCart = this.state === "cart";
      this.gen++;                       // invalidate in-flight continuations
      cancelAnimationFrame(this._cartFocusFrame);
      clearTimeout(this._cartFocusTimer);
      this._cartFocusFrame = 0;
      this._cartFocusTimer = 0;
      clearTimeout(this._coSheenT);

      // Closing mid-flight must not resurrect optimistic or retry state.
      const pendingLi = this._pending ? this.pendingNode() : null;
      if (pendingLi) this.linePendingUI(pendingLi, null);
      this._pending = null;
      if (this._changeRetry) {
        const failedLine = this.lineNodeByKey(this._changeRetry.key);
        if (failedLine) this.clearChangeError(failedLine);
      }
      this._changeRetry = null;
      this._skeleton = false;
      this._skelFailed = false;
      this.cart.classList.remove("is-updating", "gdab__cart--skel");

      // The panel keeps its semantic and visual state until the 250ms return
      // completes. Reduced motion and the cart use the immediate finalizer.
      if (wasPanel && !this.reducedMotion()) {
        this.startPanelClose();
        return;
      }
      if (wasCart) this.lingerWallets();
      this.finishCloseToPill();
    }

    nextPhrase() {
      if (!this.phrases.length) return "";
      if (!this._phraseBag.length) {
        this._phraseBag = [...this.phrases];
        for (let i = this._phraseBag.length - 1; i > 0; i -= 1) {
          const j = Math.floor(Math.random() * (i + 1));
          [this._phraseBag[i], this._phraseBag[j]] = [this._phraseBag[j], this._phraseBag[i]];
        }
        if (this._phraseBag.length > 1 && this._phraseBag[0] === this._lastPhrase) {
          [this._phraseBag[0], this._phraseBag[1]] = [this._phraseBag[1], this._phraseBag[0]];
        }
      }
      const phrase = this._phraseBag.shift();
      this._lastPhrase = phrase;
      return phrase;
    }

    selectCard(card, options = {}) {
      if (this._closing) return;
      if (card.hasAttribute("data-unavailable")) {
        const msg = this.t("variantSoldOut").replace("%s", card.dataset.name || "");
        this.announce(msg);               // dead taps must speak
        return;
      }
      const nextVariantId = card.getAttribute("data-variant-id");
      const colorChanged = !this.selectedVariant || this.selectedVariant.id !== nextVariantId;
      this.querySelectorAll(".gdab__card").forEach((c) => {
        const on = c === card;
        // aria-checked only: the cards are role="radio" (aria-pressed is
        // invalid there — axe critical) and gdab.css keys off aria-checked
        c.setAttribute("aria-checked", on ? "true" : "false");
      });
      this.selectedVariant = {
        id: nextVariantId,
        price: card.getAttribute("data-price"),
        was: "",
        priceCents: Number(card.dataset.priceCents),
        catalogPriceCents: Number(card.dataset.catalogPriceCents),
        spi: card.getAttribute("data-spi"),
        spiHead: card.getAttribute("data-spi-head"),
        spiTail: card.getAttribute("data-spi-tail"),
        name: card.getAttribute("data-name"),
        productUrl: card.getAttribute("data-product-url"),
        productHandle: card.getAttribute("data-product-handle"),
        colorIndex: card.getAttribute("data-color-index"),
        sellState: card.getAttribute("data-sell-state"),
        shipDate: card.getAttribute("data-ship-date"),
        checkoutLabel: card.dataset.checkoutLabel,
      };
      if (this.form) {
        const idInput = this.form.querySelector('[name="id"]');
        if (idInput) idInput.value = this.selectedVariant.id;
      }
      this.updatePanelProduct(card, { animate: Boolean(options.animate && colorChanged) });
      // the PILL beneath mirrors the selection (Jackson, Aug 9: closing
      // returns to prices) — the confirm row carries no offer any more
      // (Aug 26: prices live on the cards; the row is Add to cart + the
      // dynamic checkout button)
      const v = this.selectedVariant;
      [this.$(".gdab__pill .gdab__offer")].forEach((zone) => {
        if (!zone) return;
        const p = zone.querySelector(".gdab__price");
        let w = zone.querySelector(".gdab__was");
        let s = zone.querySelector(".gdab__sub");
        const f = zone.querySelector(".gdab__from");
        this.tick(p, v.price);
        // the server may not have rendered a strike/SPI node (pill_p had
        // none) while the SELECTED card carries one — create it (QA find)
        if (!w && v.was && p) {
          w = document.createElement("s");
          w.className = "gdab__was";
          p.parentElement.appendChild(w);
        }
        if (!s && v.spi) {
          s = document.createElement("span");
          s.className = "gdab__sub";
          zone.querySelector(".gdab__line1").insertAdjacentElement("afterend", s);
        }
        if (w) {
          w.style.display = v.was ? "" : "none";
          if (v.was) this.tick(w, v.was);
        }
        if (s && v.spi) {
          s.style.display = "";
          this.tick(s, v.spiHead || v.spi);
        } else if (s) {
          clearTimeout(s.__gdabTickT);
          s.dataset.tickText = "";
          s.style.width = "";
          s.style.display = "none";
          s.replaceChildren();
        }
        if (f) f.style.display = "none";     // a chosen color has ONE price
      });
      this.positionPlate(card);
      // fortune-cookie phrase renders INSIDE the selected card's strip
      // (Figma); other cards' strips are cleared
      this.panel.setAttribute("data-selected", "");
      if (colorChanged) {
        this.querySelectorAll(".gdab__card-phrase").forEach((ph) => {
          ph.textContent = "";
        });
        const slot = card.querySelector(".gdab__card-phrase");
        if (slot) slot.textContent = this.nextPhrase();
      }
      const pillFrom = this.$(".gdab__pill .gdab__from");
      if (pillFrom) pillFrom.style.display = "none";
      // the invite chip ("Choose color", general routes) retires on the
      // first pick: drop the class so the divider+sub reappear for the
      // mode morph below (Jackson, Aug 26)
      const chipBox = this.$(".gdab__pill .gdab__chip");
      if (chipBox) chipBox.classList.remove("gdab__chip--choose");
      // MIXED STATES: the chip morphs to the SELECTED product's own mode
      // (Buy w/ computed arrival vs Pre-order w/ ship week)
      const cl = card.dataset.chipLabel, cs = card.dataset.chipSub;
      if (cl) this.tick(this.$(".gdab__pill .gdab__chip-label"), cl);
      if (cs) {
        const lines = cs.split("\n").map((x) => x.trim()).filter(Boolean);
        this.tick(this.$(".gdab__pill .gdab__chip-sub"), lines.join(" "), (t) => {
          lines.forEach((ln, i) => {
            if (i) t.append(document.createElement("br"));
            t.append(ln);
          });
        });
      }
      this.confirmBtn.classList.add("is-ready");
      this.confirmBtn.removeAttribute("aria-disabled");
      // dynamic checkout: reveal the SELECTED product's own payment form
      // (each colourway ships one — the Shopify button is bound to its
      // form) and retire the gray placeholder. Unknown selection ids
      // leave the placeholder up rather than showing the wrong buyer.
      const buyForms = this.querySelectorAll(".gdab__buy-form");
      let buyLive = false;
      buyForms.forEach((f) => {
        const formVariant = f.querySelector('input[name="id"]')?.value || "";
        const on = formVariant === String(this.selectedVariant.id);
        f.hidden = !on;
        if (on) buyLive = true;
      });
      const buyPh = this.$(".gdab__buy-placeholder");
      if (buyPh) buyPh.hidden = buyLive;
      this.setShopPayState("pending");
      this.queueShopPayCheck();
      const nextSellState = card.dataset.sellState === "preorder" ? "preorder" : "stock";
      if (nextSellState !== this.cfg.sellState) {
        this.cfg.sellState = nextSellState;
        this.messages = this.allMessages.filter((message) =>
          !message.when || message.when === "always" || message.when === nextSellState);
        this.cycleIdx = 0;
      }
      if (!options.silent) {
        this.announce(this.t("colorSelected").replace("%s", this.selectedVariant.name));
        this.emit("gdab:color-select", {
          variantId: this.selectedVariant.id,
          productUrl: this.selectedVariant.productUrl,
          productHandle: this.selectedVariant.productHandle,
          colorIndex: this.selectedVariant.colorIndex,
        });
      }
    }

    updatePanelProduct(card, options = {}) {
      const write = (selector, value) => {
        const node = this.$(selector);
        if (node) node.textContent = value || "";
      };
      write("[data-panel-mode]", card.dataset.mode);
      write("[data-panel-price]", card.dataset.price);
      window.NexPreorderPerks?.sync(this, card);

      const image = this.$(".gdab__product-image");
      if (image && card.dataset.image) {
        image.src = card.dataset.image;
        image.alt = card.dataset.name || "";
      }
      const checkoutVariant = this.$("[data-gdab-checkout-variant]");
      if (checkoutVariant) checkoutVariant.value = card.dataset.variantId || "";
      if (card.dataset.checkoutLabel && !this._checkoutAttempt)
        this.confirmBtn.textContent = card.dataset.checkoutLabel;
      if (this.checkoutError) this.checkoutError.hidden = true;

      const shell = this.$(".gdab__product-shell");
      if (shell && options.animate && !this.reducedMotion()) {
        shell.classList.remove("is-color-changing");
        void shell.offsetWidth;
        shell.classList.add("is-color-changing");
        clearTimeout(this._panelColorT);
        this._panelColorT = setTimeout(
          () => shell.classList.remove("is-color-changing"), 380);
      }
    }

    // The sliding plate: one element whose clip-path window travels to the
    // selected card (color morphing in flight). First selection appears in
    // place — no slide in from the row's edge.
    positionPlate(card) {
      const plate = this.$(".gdab__card-plate");
      if (!plate || !card) return;
      const cont = plate.parentElement;
      const left = Math.max(0, card.offsetLeft);
      const right = Math.max(0, cont.clientWidth - card.offsetLeft - card.offsetWidth);
      const first = !plate.classList.contains("is-active");
      if (first) plate.style.transition = "none";
      plate.style.clipPath = `inset(0 ${right}px 0 ${left}px round 12px)`;
      const color = card.style.getPropertyValue("--card-color").trim();
      if (color) plate.style.backgroundColor = color;
      if (first) {
        void plate.offsetWidth;
        plate.style.transition = "";
        plate.classList.add("is-active");
      }
    }

    // ------------------------------------------------------------- confirm
    // INSTANT OPEN (Jackson, Aug 12): tapping Add to cart starts the
    // panel-collapse + cart-open animation the SAME frame — the known
    // selection renders as a pending line and the add POST runs in
    // parallel. The old flow parked the shopper on a grayed button for
    // the whole round-trip with zero feedback. This inverts the old
    // invariant: the add can now fail AFTER the cart is open — the
    // pending line becomes a retryable error (message + Retry) and the
    // money surfaces (totals, badge, checkout) fall back to server
    // truth, so a failed line can never read as purchased.
    resetCheckout() {
      const attempt = this._checkoutAttempt;
      if (!attempt) return;
      this._checkoutAttempt = null;
      attempt.controller.abort();
      this.confirmBtn.disabled = false;
      this.confirmBtn.removeAttribute("aria-busy");
      this.confirmBtn.textContent = this.selectedVariant?.checkoutLabel || attempt.label;
    }

    async confirm(event) {
      if (event) event.preventDefault();
      if (!this.selectedVariant) {
        this.announce(this.t("pickColor"));
        return;
      }
      if (this.state !== "panel" || this.confirmBtn.disabled || this._checkoutAttempt) return;
      if (window.NexDock?.handlesPurchase?.(this)) return window.NexDock.checkout(this);
      const attempt = {
        label: this.confirmBtn.textContent,
        controller: new AbortController(),
        navigating: false,
      };
      this._checkoutAttempt = attempt;
      this.beginCartMutation();
      this.confirmBtn.disabled = true;
      this.confirmBtn.setAttribute("aria-busy", "true");
      this.confirmBtn.textContent = "Opening checkout…";
      if (this.checkoutError) this.checkoutError.hidden = true;
      try {
        const item = { id: Number(this.selectedVariant.id), quantity: 1 };
        if (this._dockGuardAddedVariant !== String(item.id)) {
          const response = await fetch(this.cfg.addUrl, {
            method: "POST",
            credentials: "same-origin",
            signal: attempt.controller.signal,
            headers: { "Content-Type": "application/json", "Accept": "application/json" },
            body: JSON.stringify({
              items: [item],
            }),
          });
          if (!response.ok) {
            let message = this.t("addError");
            try {
              const payload = await response.json();
              message = payload.description || payload.message || message;
            } catch (error) { /* response text is optional */ }
            throw new Error(message);
          }
          this._dockGuardAddedVariant = String(item.id);
        }
        if (this._checkoutAttempt !== attempt || !this.isConnected) return;
        // A Dock retained from an older visit must be removed in the cart first.
        // Keep the successful add across a failed check so Retry cannot add it twice.
        const cartController = new AbortController();
        const cancelCart = () => cartController.abort();
        attempt.controller.signal.addEventListener("abort", cancelCart, { once: true });
        const cartTimeout = setTimeout(cancelCart, 6000);
        let cart;
        try {
          const cartResponse = await fetch(this.cfg.cartUrl, {
            credentials: "same-origin", cache: "no-store",
            headers: { Accept: "application/json" }, signal: cartController.signal,
          });
          if (!cartResponse.ok) throw new Error("cart-check-failed");
          cart = await cartResponse.json();
          if (!Array.isArray(cart?.items)) throw new Error("cart-check-failed");
        } catch (error) {
          throw new Error(this.cfg.cartCheckError);
        } finally {
          clearTimeout(cartTimeout);
          attempt.controller.signal.removeEventListener("abort", cancelCart);
        }
        if (this._checkoutAttempt !== attempt || !this.isConnected) return;
        this._dockGuardAddedVariant = null;
        attempt.navigating = true;
        window.location.assign(cart.items.some(item => this.isDockCartItem(item))
          ? this.cfg.cartPageUrl : this.cfg.checkoutUrl);
      } catch (error) {
        if (this._checkoutAttempt !== attempt) return;
        this.resetCheckout();
        const message = error && error.message ? error.message : this.t("addError");
        if (this.checkoutError) {
          this.checkoutError.textContent = message;
          this.checkoutError.hidden = false;
        }
        this.announce(message);
      } finally {
        this.endCartMutation();
      }
    }

    // optimistic view: last-known cart + the selection as a pending line.
    // Provisional render — _lastCart (local truth) is NOT overwritten by
    // a cart the server hasn't confirmed.
    beginPending(variant) {
      const current = (this._lastCart && this._lastCart.items || [])
        .find((item) => String(item.variant_id || item.id) === String(variant.id));
      this._pending = {
        variant,
        key: String(variant.id),
        baselineQty: current ? Number(current.quantity) || 0 : 0,
        baselineVerified: false,
      };
      this.renderCart(this.mergedCart(variant, null), true);
      const li = this.pendingNode();
      if (li) this.linePendingUI(li, "pending");
      // every stepper parks while the add is unresolved (same affordance
      // as a qty change in flight)
      this.cart.classList.add("is-updating");
    }
    pendingNode() {
      const key = this._pending && this._pending.key;
      if (!key) return null;
      return this.lineNodeByKey(key);
    }
    lineNodeByKey(key) {
      return [...this.querySelectorAll(".gdab__cart-line")]
        .find((n) => n.dataset.lineKey === String(key)) || null;
    }
    // pending/failed dress on a line. mode: "pending" | "failed" | null;
    // own=true marks a failed INCREMENT on a line the shopper already
    // owns units of — its stepper stays visible (the owned qty is real).
    // Class swaps only — the veil dissolves by CSS transition when the
    // class drops (never a straight cut, standing rule).
    linePendingUI(li, mode, own) {
      li.classList.toggle("gdab__cart-line--pending", mode === "pending");
      li.classList.toggle("gdab__cart-line--failed", mode === "failed");
      li.classList.toggle("gdab__cart-line--failed-own", mode === "failed" && Boolean(own));
      const wait = li.querySelector(".gdab__cart-line-wait");
      const fail = li.querySelector(".gdab__cart-line-fail");
      if (wait) {
        wait.hidden = mode !== "pending";
        if (mode === "pending") {
          const lb = wait.querySelector(".gdab__cart-line-wait-label");
          if (lb) lb.textContent = this.t("adding");
        }
      }
      if (fail) {
        fail.hidden = mode !== "failed";
        if (mode === "failed") {
          const ms = fail.querySelector(".gdab__cart-line-fail-msg");
          const rt = fail.querySelector(".gdab__line-retry");
          if (ms) ms.textContent = this.t("addError");
          if (rt) rt.textContent = this.t("retry");
        }
      }
      li.querySelectorAll(".gdab__step").forEach((b) => {
        b.disabled = mode !== null || !li.dataset.lineKey;
      });
    }

    /* A network error does not prove Shopify rejected a non-idempotent
       /cart/add request: the response may simply have been lost. Read cart
       truth before ever offering another POST, and compare against the
       quantity captured before the original add. */
    async readCartTruth() {
      try {
        const res = await fetch(this.cfg.cartUrl, { cache: "no-store", headers: { Accept: "application/json" } });
        if (!res.ok) return null;
        const cart = await res.json().catch(() => null);
        return cart && Array.isArray(cart.items) ? cart : null;
      } catch (err) {
        return null;
      }
    }

    async verifyPendingBaseline(variant, gen) {
      const cart = await this.readCartTruth();
      if (gen !== this.gen || !cart || !this._pending) return false;
      const real = cart.items.find((item) =>
        String(item.variant_id || item.id) === String(variant.id));
      this._lastCart = cart;
      this._pending.baselineQty = real ? Number(real.quantity) || 0 : 0;
      this._pending.baselineVerified = true;
      /* Correct the optimistic first-session row against authoritative cart
         truth before the non-idempotent POST is allowed onto the network. */
      this.renderCart(this.mergedCart(variant, null), true);
      const node = this.pendingNode();
      if (node) this.linePendingUI(node, "pending");
      this.cart.classList.add("is-updating");
      return true;
    }

    async inspectPendingAdd(variant, pending) {
      if (!pending || !pending.baselineVerified)
        return { status: "unknown", cart: null, real: null };
      const cart = await this.readCartTruth();
      if (!cart) return { status: "unknown", cart: null, real: null };
      try {
        const real = cart.items.find((item) =>
          String(item.variant_id || item.id) === String(variant.id));
        const now = real ? Number(real.quantity) || 0 : 0;
        const before = Number(pending.baselineQty) || 0;
        return { status: now >= before + 1 ? "confirmed" : "absent", cart, real };
      } catch (err) {
        return { status: "unknown", cart: null, real: null };
      }
    }

    completeAdd(variant, added, cart) {
      this.resolvePending(variant, added, cart);
      this.emit("gdab:add", { variantId: variant.id, cart });
      if (window.Shopify && Shopify.actions && Shopify.actions.updateCart && cart) {
        try { Shopify.actions.updateCart(cart); } catch (e) { /* theme hook */ }
      }
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:refresh", { bubbles: true, detail: { cart } }));
    }

    async runAdd(variant, gen) {
      this.beginCartMutation();
      try {
        await this.runPendingAdd(variant, gen);
      } finally {
        this.endCartMutation();
      }
    }

    async runPendingAdd(variant, gen) {
      // documented items[] form (the legacy flat body is unspecced — QA find)
      const body = { items: [{ id: Number(variant.id), quantity: 1 }] };

      /* Establish a real pre-POST quantity even on a cold page whose header
         only knew the aggregate badge. Without this baseline an existing
         quantity of 1 could be mistaken for a successfully lost response. */
      if (!this._pending || !this._pending.baselineVerified) {
        const baselineReady = await this.verifyPendingBaseline(variant, gen);
        if (!baselineReady) {
          if (gen !== this.gen) return;
          this.failPending(variant);
          this.announce(this.t("updateError"));
          return;                            // no POST without known truth
        }
      }

      // DOMAIN 1 — the add itself. The cart is ALREADY open on a pending
      // line; failure here becomes a retryable error ON that line.
      let added;
      try {
        const res = await fetch(this.cfg.addUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error("add-failed:" + res.status);
        const aj = await res.json().catch(() => null);
        added = aj && Array.isArray(aj.items) ? aj.items[0] : aj;
      } catch (err) {
        if (gen !== this.gen) return;          // cart closed mid-flight:
                                               // nothing resurrects
        const pending = this._pending;
        const check = await this.inspectPendingAdd(variant, pending);
        if (gen !== this.gen) return;
        if (check.status === "confirmed") {
          this.completeAdd(variant, check.real, check.cart);
          return;
        }
        if (check.cart) this._lastCart = check.cart;
        this.failPending(variant);
        this.announce(this.t("addError"));
        this.emit("gdab:error", { error: String(err) });
        return;
      }

      // DOMAIN 2 — refreshing the cart view. The item IS in the cart now;
      // never report an add failure from here.
      let cart = null;
      try {
        const res = await fetch(this.cfg.cartUrl, { cache: "no-store", headers: { Accept: "application/json" } });
        if (res.ok) {
          // a 429/430 throttle body is truthy JSON but not a cart — rendering
          // it right after a successful add is the double-order state (QA major)
          const parsed = await res.json().catch(() => null);
          if (parsed && Array.isArray(parsed.items)) cart = parsed;
        }
      } catch (err) { /* degrade below */ }

      if (gen !== this.gen) {
        // user closed the cart mid-flight: the add stands (item is in the
        // cart, badge syncs), but no UI is resurrected
        if (cart) { this._lastCart = cart; this.syncBadge(cart); }
        this.emit("gdab:add", { variantId: variant.id, cart });
        return;
      }

      this.completeAdd(variant, added, cart);
    }

    // the add landed: the pending veil dissolves INTO the real line. The
    // live node adopts the server's line key BEFORE the refresh render so
    // renderCart PATCHES it in place — same node, tick animations, never
    // a remove+append straight cut.
    resolvePending(variant, added, cart) {
      const vid = String((added && (added.variant_id || added.id)) || variant.id);
      const li = this.pendingNode();
      const real = cart &&
        (cart.items || []).find((i) => String(i.variant_id || i.id) === vid);
      const newKey = real && real.key ? String(real.key)
        : (added && added.key ? String(added.key) : null);
      if (li && newKey) li.dataset.lineKey = newKey;
      this._pending = null;
      this.cart.classList.remove("is-updating");
      if (cart && real) this.renderCart(cart);
      else this.renderCartFallback(variant, added);
      const node = li && li.isConnected ? li
        : this.lineNodeByKey(newKey || vid);
      if (node) this.linePendingUI(node, null);
      this.announce(this.t("added"));
    }

    // the add FAILED with the cart open: retryable error on the line,
    // money surfaces roll back to server truth. Two shapes:
    // - the variant already had a real line (the +1 failed): re-render
    //   truth — the qty ticks back down in place, then the line dresses
    //   as failed;
    // - a brand-new line: keep the client-owned node (renderCart skips
    //   removing the pending key) and roll back totals/badge only.
    failPending(variant) {
      if (this._pending) this._pending.failed = true;
      this.cart.classList.remove("is-updating");
      const li = this.pendingNode();
      const truth = this._lastCart || { items: [], item_count: 0, total_price: 0 };
      const inTruth = (truth.items || []).some((i) =>
        String(i.variant_id || i.id) === String(variant.id));
      if (inTruth) {
        this.renderCart(truth, true);
      } else {
        this.renderTotals(truth);
        this.syncBadge(truth);
      }
      const node = li && li.isConnected ? li : this.pendingNode();
      if (node) this.linePendingUI(node, "failed", inTruth);
    }

    async retryAdd() {
      const p = this._pending;
      if (!p || !p.failed || this.state !== "cart") return;
      const variant = p.variant;
      const gen = this.gen;
      p.failed = false;                     // park repeated Retry taps
      const node = this.pendingNode();
      if (node) this.linePendingUI(node, "pending");
      this.cart.classList.add("is-updating");
      /* A failed baseline GET means no add POST was sent, so this retry can
         safely restart the guarded flow and establish truth first. */
      if (!p.baselineVerified) {
        this.beginPending(variant);
        this.runAdd(variant, gen);
        return;
      }
      const check = await this.inspectPendingAdd(variant, p);
      if (gen !== this.gen) return;
      if (check.status === "confirmed") {
        this.completeAdd(variant, check.real, check.cart);
        return;
      }
      if (check.status === "unknown") {
        p.failed = true;
        this.failPending(variant);
        this.announce(this.t("updateError"));
        return;                              // never risk a duplicate POST
      }
      this._lastCart = check.cart;           // valid truth: retry is safe
      this.beginPending(variant);   // same node re-dresses as pending —
                                    // the error row swaps for the veil
      this.runAdd(variant, gen);
    }

    clearChangeError(line) {
      if (!line) return;
      line.classList.remove("gdab__cart-line--failed", "gdab__cart-line--failed-own");
      const fail = line.querySelector(".gdab__cart-line-fail");
      if (fail) fail.hidden = true;
    }

    async retryChange() {
      const retry = this._changeRetry;
      if (!retry || this.state !== "cart" || this._changing) return;
      const line = this.lineNodeByKey(retry.key);
      if (!line) {
        this.$(".gdab__cart-close").focus();
        this._changing = true;
        this.beginCartMutation();
        if (this._lastCart) this.renderTotals(this._lastCart);
        try {
          const cart = await this.readCartTruth();
          if (!cart) throw new Error(this.t("updateError"));
          this._changeRetry = null;
          this.renderCart(cart);
        } catch (error) {
          this._changeRetry = { ...retry, message: error.message || this.t("updateError") };
          this.announce(this._changeRetry.message);
        } finally {
          this._changing = false;
          this.endCartMutation();
          if (this._lastCart) this.renderTotals(this._lastCart);
        }
        return;
      }
      this._changeRetry = null;
      this.clearChangeError(line);
      const target = retry.focusClass && line.querySelector(retry.focusClass);
      (target || line.querySelector(".gdab__step") || this.$(".gdab__cart-close")).focus();
      this.changeLine(line, retry.quantity);
    }

    async changeLine(line, quantity) {
      // A new deliberate change supersedes an older failed request. Clear
      // its visual retry state so two different lines never claim ownership
      // of the single delegated Retry control at the same time.
      if (this._changeRetry) {
        const old = this.lineNodeByKey(this._changeRetry.key);
        if (old) this.clearChangeError(old);
        this._changeRetry = null;
      }
      this._changing = true;
      this.beginCartMutation();
      if (this._lastCart) this.renderTotals(this._lastCart);
      const gen = this.gen;                    // stale continuations must die
      const key = line.dataset.lineKey;
      const want = Math.max(0, quantity);
      const active = document.activeElement;
      const restoreFocus = Boolean(active && line.contains(active));
      const activeClass = active && active.classList
        ? (active.classList.contains("gdab__step--plus") ? ".gdab__step--plus"
          : active.classList.contains("gdab__step--minus") ? ".gdab__step--minus"
          : null)
        : null;
      const lineIndex = [...this.$(".gdab__cart-lines")
        .querySelectorAll(".gdab__cart-line")].indexOf(line);
      this.cart.classList.add("is-updating");  // steppers dim: taps are parked
      line.classList.add("is-busy");
      try {
        let cart;
        const item = this._lastCart?.items?.find(entry => String(entry.key) === String(key));
        const dockChange = item && window.NexDock?.isDock(item) && window.NexDock.changeDockQuantity;
        const bagChange = item && window.NexDock?.isBag?.(item) && window.NexDock.changeBagQuantity;
        if (typeof (dockChange || bagChange) === "function") {
          try {
            cart = await (dockChange || bagChange)(this._lastCart, key, want);
          } catch (error) {
            error.desc ||= error.message;
            if (error.cart) this.renderCart(error.cart);
            throw error;
          }
        } else {
          const res = await fetch(this.cfg.changeUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ id: key, quantity: want }),
          });
          if (!res.ok) {
            const errBody = await res.json().catch(() => null);
            const e = new Error("change-failed:" + res.status);
            e.desc = errBody && errBody.description;
            throw e;
          }
          cart = await res.json().catch(() => null);
        }
        if (gen !== this.gen) return;          // sheet closed mid-flight
        if (!cart || !Array.isArray(cart.items)) throw new Error("bad-cart");
        this.renderCart(cart);
        // Removing a keyboard-focused line otherwise drops focus to <body>,
        // outside the modal: Tab can escape the trap and Escape no longer
        // reaches this component. Keep focus on the same keyed line after a
        // structural rebuild, else the adjacent line, else the close button.
        if (restoreFocus && this.state === "cart") {
          const lines = [...this.$(".gdab__cart-lines")
            .querySelectorAll(".gdab__cart-line")];
          const same = this.lineNodeByKey(key);
          const neighbour = lines[Math.min(Math.max(lineIndex, 0), lines.length - 1)];
          const targetLine = same || neighbour;
          const target = targetLine && activeClass
            ? targetLine.querySelector(activeClass)
            : null;
          (target || (targetLine && targetLine.querySelector(".gdab__step")) ||
            this.$(".gdab__cart-close")).focus();
        }
        // announce the SERVER's answer, not the request: /cart/change.js
        // silently clamps at available inventory (QA find)
        const now = cart.items.find((i) => String(i.key || i.variant_id || i.id) === String(key));
        if (!now) {
          this.announce(this.t("lineRemoved"));
        } else if (want > now.quantity) {
          this.announce(this.t("stockCapped").replace("%s", String(now.quantity)));
          const stockEl = line.isConnected && line.querySelector(".gdab__cart-line-stock");
          if (stockEl) {           // sighted feedback for the silent clamp
            stockEl.classList.add("is-limit");
            setTimeout(() => stockEl.classList.remove("is-limit"), 1200);
          }
        } else {
          this.announce(this.t("qtyNow").replace("%s", String(now.quantity)));
        }
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:refresh", { bubbles: true, detail: { cart } }));
      } catch (err) {
        if (gen === this.gen) {
          const message = err.desc || this.t("updateError");
          this._changeRetry = { key, quantity: want, focusClass: activeClass, message };
          const liveLine = this.lineNodeByKey(key) || line;
          liveLine.classList.add("gdab__cart-line--failed", "gdab__cart-line--failed-own");
          const fail = liveLine.querySelector(".gdab__cart-line-fail");
          if (fail) {
            const msg = fail.querySelector(".gdab__cart-line-fail-msg");
            const retry = fail.querySelector(".gdab__line-retry");
            if (msg) msg.textContent = message;
            if (retry) retry.textContent = this.t("retry");
            fail.hidden = false;
            if (restoreFocus && retry) retry.focus();
          }
          this.announce(message);
        }
      } finally {
        line.classList.remove("is-busy");
        this.cart.classList.remove("is-updating");
        this._changing = false;
        this.endCartMutation();
        if (this._lastCart) this.renderTotals(this._lastCart);
      }
    }

    // header cart entry: open the sheet straight from the icon (the
    // sheet's diagonal clip-path already originates at the top-right,
    // i.e. from the icon's own corner).
    // INSTANT OPEN (Jackson, Aug 13): the open animation starts the SAME
    // frame as the tap, unconditionally — the ATC grammar, applied here.
    // Content is the best local truth at that instant: the last confirmed
    // cart if one exists; else skeleton rows in the ATC pending-line
    // dress when the badge says the cart has items (fresh pageload); else
    // the empty state. /cart.js rides in PARALLEL and reconciles in place
    // — renderCart patches nodes by key, so skeletons dissolve into real
    // lines, never a straight cut. The old fetch-then-open wait (up to 4s
    // on a hung request) is gone; the abort now only caps how long the
    // placeholders slide before the quiet retry state.
    openCartDirect() {
      if (this.state !== "pill") return;
      const gen = this.gen;
      this.lastFocus = document.activeElement;
      this.lockPage(true);
      if (this._lastCart) {
        // confirmed truth once — render whole, checkout stays live; the
        // refresh will tick any drift into the same nodes on arrival
        this.renderCart(this._lastCart);
      } else {
        const badge = document.querySelector("[data-cart-count]");
        const count = badge
          ? parseInt(badge.dataset.tickText || badge.textContent, 10) || 0
          : 0;
        if (count > 0) this.renderSkeleton(count);
        else this.renderCart({ items: [], item_count: 0, total_price: 0 });
      }
      this.openCart();
      this.refreshOpenCart(gen);
    }

    // skeleton content for a fresh pageload whose badge shows items: no
    // local cart exists to render, so the sheet opens on placeholder rows
    // wearing the EXACT pending dress the optimistic add uses (dimmed row
    // + sliding orange hairline — ONE loading language, no second look).
    // Rows are aria-hidden (nothing to say yet) and checkout stays dead
    // until server truth lands (provisional-content rule).
    renderSkeleton(count) {
      this._skeleton = true;
      this._skelFailed = false;
      const lines = this.$(".gdab__cart-lines");
      // no confirmed cart exists when this path runs — anything present
      // is stale client debris (e.g. an undressed failed line from a
      // pre-cache session that never resolved)
      lines.querySelectorAll(".gdab__cart-line").forEach((n) => n.remove());
      // the badge counts UNITS; lines collapse quantities, so cap low —
      // extras dissolve out on reconcile anyway
      const n = Math.min(count, 3);
      for (let i = 0; i < n; i++) lines.appendChild(this.skeletonLineEl(i));
      this.cart.classList.remove("is-empty");
      const emptyEl = this.$(".gdab__cart-empty");
      if (emptyEl) emptyEl.hidden = true;
      const checkout = this.$(".gdab__checkout");
      if (checkout) {
        checkout.classList.add("is-disabled");
        checkout.disabled = true;
        checkout.setAttribute("aria-disabled", "true");
        checkout.setAttribute("tabindex", "-1");
      }
      const note = this.$("[data-cart-checkout-note]");
      if (note) note.hidden = true;
      const spi = this.$(".gdab__cart-spi");
      if (spi) spi.textContent = "";
      const totalWas = this.$(".gdab__cart-total-was");
      if (totalWas) totalWas.hidden = true;
      this.cart.classList.add("gdab__cart--skel");   // total-value bar
    }
    skeletonLineEl(i) {
      const tpl = this.$("template.gdab__line-tpl");
      const li = tpl.content.firstElementChild.cloneNode(true);
      li.classList.add("gdab__cart-line--skel");
      li.dataset.lineKey = "skel:" + i;
      li.dataset.qty = "1";
      li.setAttribute("aria-hidden", "true");
      const img = li.querySelector("img");
      img.alt = "";
      img.style.visibility = "hidden";     // beige box carries the shape
      li.querySelector(".gdab__cart-qty").textContent = "";
      li.querySelectorAll(".gdab__step").forEach((b) => {
        b.disabled = true;
        b.setAttribute("tabindex", "-1");
      });
      this.dressSkeleton(li);
      return li;
    }
    // the ATC pending dress minus the word: this is a LOAD, not an add —
    // "Adding…" would lie, so the label stays empty and the sliding
    // hairline alone speaks
    dressSkeleton(li) {
      this.linePendingUI(li, "pending");
      const lb = li.querySelector(".gdab__cart-line-wait-label");
      if (lb) lb.textContent = "";
    }

    cartMutationActive() {
      return Boolean(this._cartMutationDepth || this._changing ||
        (this._pending && !this._pending.failed) || this._checkoutAttempt ||
        document.querySelector(".hero-purchase__checkout[aria-busy='true']"));
    }

    cancelCartRefresh() {
      clearTimeout(this._cartRefreshTimer);
      this._cartRefreshTimer = 0;
      if (this._cartRead) this._cartRead.controller.abort();
      this._cartRead = null;
    }

    beginCartMutation() {
      this._cartMutationDepth = (this._cartMutationDepth || 0) + 1;
      this._cartEpoch = (this._cartEpoch || 0) + 1;
      this.cancelCartRefresh();
    }

    endCartMutation() {
      this._cartMutationDepth = Math.max(0, (this._cartMutationDepth || 0) - 1);
      this.queueCartRefresh();
    }

    queueCartRefresh(delay = 80) {
      clearTimeout(this._cartRefreshTimer);
      this._cartRefreshTimer = 0;
      if (!this.__armed || !this.isConnected || document.visibilityState === "hidden") return;
      this._cartRefreshTimer = setTimeout(() => {
        this._cartRefreshTimer = 0;
        this.refreshOpenCart();
      }, delay);
    }

    refreshOpenCart() {
      if (!this.__armed || !this.isConnected || document.visibilityState === "hidden") return;
      if (this.cartMutationActive()) {
        this.queueCartRefresh(250);
        return;
      }
      const seq = this._truthSeq;
      const epoch = this._cartEpoch || 0;
      if (this._cartRead && this._cartRead.seq === seq && this._cartRead.epoch === epoch)
        return this._cartRead.promise;
      this.cancelCartRefresh();
      const read = { seq, epoch, controller: new AbortController(), promise: null };
      this._cartRead = read;
      read.promise = (async () => {
        const timer = setTimeout(() => read.controller.abort(), 4000);
        let cart = null;
        try {
          const response = await fetch(this.cfg.cartUrl, {
            credentials: "same-origin",
            cache: "no-store",
            headers: { Accept: "application/json" },
            signal: read.controller.signal,
          });
          if (response.ok) {
            const parsed = await response.json();
            if (parsed && Array.isArray(parsed.items) &&
              Number.isSafeInteger(parsed.item_count) && parsed.item_count >= 0) cart = parsed;
          }
        } catch (error) { /* Retain the last confirmed cart if unavailable. */ }
        finally { clearTimeout(timer); }
        if (this._cartRead !== read) return;
        this._cartRead = null;
        if (!this.__armed || !this.isConnected ||
          this._truthSeq !== seq || (this._cartEpoch || 0) !== epoch) return;
        if (this.cartMutationActive()) {
          this.queueCartRefresh(250);
          return;
        }
        if (read.controller.signal.aborted) cart = null;
        if (cart) {
          if (this.state === "cart") {
            this.adoptSkeletons(cart);
            this.renderCart(cart);
            this.settleSkeletons();
          } else {
            this._lastCart = cart;
            this._truthSeq = (this._truthSeq || 0) + 1;
            this.syncBadge(cart);
          }
        } else if (this.state === "cart" && this._skeleton) this.failSkeleton();
        return cart;
      })();
      return read.promise;
    }

    // stamp incoming line identities onto the skeleton rows (in order) so
    // renderCart patches those very nodes — the dissolve happens on the
    // SAME element, never remove+append. Structure (strike/stock rows) is
    // pre-shaped to each incoming item so updateLine's drift check keeps
    // patching instead of rebuilding.
    adoptSkeletons(cart) {
      if (!this._skeleton) return;
      const items = (cart && cart.items) || [];
      this.querySelectorAll(".gdab__cart-line--skel").forEach((li, i) => {
        const item = items[i];
        if (!item) {
          // badge over-counted (qty > 1 collapses onto one line): the
          // extra placeholder dissolves out and owns its own removal —
          // renderCart's sweep leaves vanishing nodes to finish
          li.classList.add("gdab__cart-line--vanish");
          setTimeout(() => li.remove(), 260);
          return;
        }
        const vid = String(item.variant_id || item.id);
        li.dataset.lineKey = String(item.key || vid);
        const qty = item.quantity || 1;
        const unit = item.final_price ?? item.price
          ?? Math.round((item.final_line_price ?? item.line_price ?? 0) / qty);
        if (!((item.original_price || 0) > unit)) {
          const w = li.querySelector(".gdab__was");
          if (w) w.remove();
        }
        if (!this.stockMap[vid]) {
          const st = li.querySelector(".gdab__cart-line-stock");
          if (st) st.remove();
        }
        if (!this.lineCancelText(item)) {
          const cn = li.querySelector(".gdab__cart-line-cancel");
          if (cn) cn.remove();
        }
        if (!this.lineSaveText(vid, unit, qty)) {
          const sv = li.querySelector(".gdab__cart-line-save");
          if (sv) sv.remove();
        }
      });
    }
    // truth landed and renderCart has already ticked it into the adopted
    // nodes: drop the skeleton dress — the 200ms opacity transitions turn
    // the veil lift into the dissolve (never a straight cut)
    settleSkeletons() {
      if (!this._skeleton) return;
      this._skeleton = false;
      this._skelFailed = false;
      this.cart.classList.remove("gdab__cart--skel");
      this.querySelectorAll(".gdab__cart-line--skel").forEach((li) => {
        if (li.classList.contains("gdab__cart-line--vanish")) return;
        li.classList.remove("gdab__cart-line--skel");
        li.removeAttribute("aria-hidden");
        this.linePendingUI(li, null);
        // the steppers wake with the row: template labels get the same
        // per-line product suffix lineEl stamps ("… — M1 CHEST BAG")
        const nameEl = li.querySelector(".gdab__cart-line-name");
        const nm = nameEl
          ? (nameEl.dataset.tickText || nameEl.textContent).trim() : "";
        li.querySelectorAll(".gdab__step").forEach((b) => {
          b.removeAttribute("tabindex");
          if (nm) b.setAttribute("aria-label",
            b.getAttribute("aria-label") + " — " + nm);
        });
      });
    }
    // the refresh died with only placeholders on screen: ONE quiet
    // retryable row (the ATC failed-line dress, the update-error words),
    // the rest dissolve away. No cached truth exists, so checkout stays
    // dead until a retry succeeds.
    failSkeleton() {
      if (!this._skeleton) return;
      this._skelFailed = true;
      let first = null;
      this.querySelectorAll(".gdab__cart-line--skel").forEach((li) => {
        if (li.classList.contains("gdab__cart-line--vanish")) return;
        if (!first) { first = li; return; }
        li.classList.add("gdab__cart-line--vanish");
        setTimeout(() => li.remove(), 260);
      });
      if (first) {
        this.linePendingUI(first, "failed");
        const ms = first.querySelector(".gdab__cart-line-fail-msg");
        if (ms) ms.textContent = this.t("updateError");   // a LOAD failed, not an add
        first.removeAttribute("aria-hidden");   // the Retry control is real
      }
      this.announce(this.t("updateError"));
    }
    retryRefresh() {
      if (!this._skelFailed || this.state !== "cart") return;
      this._skelFailed = false;
      const li = this.$(".gdab__cart-line--skel:not(.gdab__cart-line--vanish)");
      if (li) {
        // the tap target is about to hide inside an aria-hidden row —
        // focus moves to the sheet's close control first
        if (li.contains(document.activeElement)) this.$(".gdab__cart-close").focus();
        li.setAttribute("aria-hidden", "true");
        this.dressSkeleton(li);       // error row swaps back for the veil
      }
      this.refreshOpenCart(this.gen);
    }

    // Offer-section entry into the existing guarded add pipeline. Returning
    // false leaves the caller's native product form untouched (e.g. a
    // misconfigured primary product not represented by this GDAB).
    addExternalVariant(detail) {
      if (this.state !== "pill") return false;
      const variantId = String(detail.variantId || "");
      const card = [...this.querySelectorAll(".gdab__card")]
        .find((item) => item.dataset.variantId === variantId);
      if (!card || card.hasAttribute("data-unavailable")) return false;

      const focus = detail.trigger;
      this.lastFocus = focus && focus.isConnected ? focus : document.activeElement;
      this.lockPage(true);
      this.selectCard(card, { silent: true });
      if (!this.selectedVariant) {
        this.lockPage(false);
        return false;
      }

      const variant = this.selectedVariant;
      const gen = this.gen;
      this.beginPending(variant);
      this.openCart();
      this.runAdd(variant, gen);
      return true;
    }

    // ---------------------------------------------------------------- cart
    openCart() {
      this.state = "cart";
      this.setAttribute("data-gdab-state", "cart");
      // The mobile hero morph intentionally parks the compact dock with
      // inert/aria-hidden before Highlights. A header-cart or hero-card
      // entry can still open this full drawer from that phase, so the
      // dialog must temporarily own interaction independently of the pill.
      this.inert = false;
      this.removeAttribute("aria-hidden");
      this.setPillExposed(false);
      const target = this._kb ? this.$(".gdab__cart-close") : this.cart;
      const origin = this.lastFocus;
      const gen = this.gen;
      const focusCart = () => {
        if (!this.isConnected || this.state !== "cart" || this.gen !== gen ||
          this.cart.contains(document.activeElement)) return;
        const active = document.activeElement;
        if (active !== origin && active !== document.body && active !== document.documentElement) return;
        target.focus({ preventScroll: true });
      };
      focusCart();
      cancelAnimationFrame(this._cartFocusFrame);
      this._cartFocusFrame = requestAnimationFrame(() => {
        this._cartFocusFrame = 0;
        focusCart();
        if (!this.cart.contains(document.activeElement)) {
          this._cartFocusFrame = requestAnimationFrame(() => {
            this._cartFocusFrame = 0;
            focusCart();
          });
        }
      });
      clearTimeout(this._cartFocusTimer);
      this._cartFocusTimer = setTimeout(() => {
        this._cartFocusTimer = 0;
        focusCart();
      }, 300);
      this.announce(this.t("cartOpened"));
      this.scheduleCheckoutSheen();
      this.emit("gdab:cart-open");
    }

    // the GDAB trust sheen on the cart CTA, same ambient cadence
    // (Jackson, Aug 9)
    scheduleCheckoutSheen() {
      clearTimeout(this._coSheenT);
      this._coSheenT = setTimeout(() => {
        const co = this.$(".gdab__checkout");
        if (co && this.state === "cart" &&
            !co.classList.contains("is-disabled") && !this.reducedMotion()) {
          co.classList.add("gdab__checkout--sheen");
          const off = () => co.classList.remove("gdab__checkout--sheen");
          co.addEventListener("animationend", off, { once: true });
          setTimeout(off, 2400);   // hidden-tab safety: animationend can stall
        }
        if (this.state === "cart") this.scheduleCheckoutSheen();
      }, this.cfg.ambientMs);
    }

    // fixed theme assets for the M1 colorways (Jackson: decoupled from
    // Shopify photos); anything else falls back to the platform image
    isDockCartItem(item) {
      const vid = String(item?.variant_id || item?.id || "");
      return Boolean(this.cfg.dockVariantId && vid === this.cfg.dockVariantId)
        || (item?.handle || item?.product?.handle) === "chest-bag-magnetic-dock";
    }
    lineImage(title, fallback, isDock = false) {
      if (isDock) return this.cfg.imgDock || fallback || "";
      const t = (title || "").toLowerCase();
      if (t.includes("orange") && this.cfg.imgOrange) return this.cfg.imgOrange;
      if (t.includes("chest bag") && this.cfg.imgBlack) return this.cfg.imgBlack;
      return fallback || "";
    }
    lineEl(fields) {
      const tpl = this.$("template.gdab__line-tpl");
      const li = tpl.content.firstElementChild.cloneNode(true);
      li.dataset.lineKey = fields.key || "";
      li.dataset.qty = fields.qty;
      li.classList.toggle("gdab__cart-line--dock", Boolean(fields.isDock));
      // Figma shows the base name only ("M1 CHEST BAG") — the image
      // carries the colorway; strip separator suffixes
      const baseName = (fields.name || "").split(/[|\u2014\u2013]/)[0].trim();
      li.querySelector(".gdab__cart-line-name").textContent = baseName.toUpperCase();
      li.querySelector(".gdab__price").textContent = fields.price;
      const wasEl = li.querySelector(".gdab__was");
      if (fields.was) wasEl.textContent = fields.was;
      else wasEl.remove();
      // every template lookup is null-guarded: stale CACHED page HTML
      // paired with this newer file lacks the younger spans (the
      // cache-pairing class) — an unguarded .remove() here killed the
      // whole cart render for the cache window (review find)
      const stockEl = li.querySelector(".gdab__cart-line-stock");
      if (stockEl) {
        if (fields.stock) stockEl.textContent = fields.stock;
        else stockEl.remove();
      }
      const cancelEl = li.querySelector(".gdab__cart-line-cancel");
      if (cancelEl) {
        if (fields.cancel) cancelEl.textContent = fields.cancel;
        else cancelEl.remove();
      }
      const saveEl = li.querySelector(".gdab__cart-line-save");
      if (saveEl) {
        if (fields.save) saveEl.textContent = fields.save;
        else saveEl.remove();
      }
      this.renderLineShip(li, fields.ship);
      li.querySelector(".gdab__cart-qty").textContent = fields.qty;
      const img = li.querySelector("img");
      const src = this.lineImage(fields.name, fields.image, fields.isDock);
      if (src) { img.src = src; img.alt = fields.name; }
      else { img.removeAttribute("src"); img.alt = ""; img.style.visibility = "hidden"; }
      li.querySelectorAll(".gdab__step").forEach((b) => {
        // "Increase quantity — M1 CHEST BAG": identical names across lines
        // are indistinguishable to AT users (QA find)
        b.setAttribute("aria-label", b.getAttribute("aria-label") + " — " + baseName);
        // a line without a server key cannot be addressed by /cart/change.js
        if (!fields.key) b.disabled = true;
      });
      return li;
    }
    // qty/price refresh WITHOUT rebuilding the node: a rebuild destroys the
    // focused stepper mid-interaction for keyboard/VO users (QA find)
    updateLine(li, fields) {
      // structural drift (strike or stock row appearing/disappearing, e.g.
      // a mode flip between renders) can't be patched in place — rebuild
      const wasEl = li.querySelector(".gdab__was");
      const stockEl = li.querySelector(".gdab__cart-line-stock");
      const cancelEl = li.querySelector(".gdab__cart-line-cancel");
      const saveEl = li.querySelector(".gdab__cart-line-save");
      if (Boolean(fields.was) !== Boolean(wasEl) ||
          Boolean(fields.stock) !== Boolean(stockEl) ||
          Boolean(fields.cancel) !== Boolean(cancelEl) ||
          Boolean(fields.save) !== Boolean(saveEl)) {
        const active = document.activeElement;
        const restore = Boolean(active && li.contains(active));
        const activeClass = restore && active.classList
          ? (active.classList.contains("gdab__step--plus") ? ".gdab__step--plus"
            : active.classList.contains("gdab__step--minus") ? ".gdab__step--minus"
            : active.classList.contains("gdab__line-retry") ? ".gdab__line-retry"
            : null)
          : null;
        const replacement = this.lineEl(fields);
        li.replaceWith(replacement);
        if (restore) {
          const target = activeClass && replacement.querySelector(activeClass);
          (target || replacement.querySelector(FOCUSABLE) || this.$(".gdab__cart-close")).focus();
        }
        return;
      }
      li.dataset.qty = fields.qty;
      li.classList.toggle("gdab__cart-line--dock", Boolean(fields.isDock));
      this.renderLineShip(li, fields.ship);
      // the optimistic pending line only knows the VARIANT name; the
      // server's title arrives with the refresh — roll it in like any
      // other changed value (instant-open, Aug 12)
      const nameEl = li.querySelector(".gdab__cart-line-name");
      const baseName = (fields.name || "").split(/[|—–]/)[0].trim().toUpperCase();
      if (nameEl && baseName && nameEl.textContent !== baseName) this.tick(nameEl, baseName);
      // ...and so may the image: an optimistic line whose name resolved
      // no artwork heals here once the server title/image lands
      const img = li.querySelector("img");
      if (img) {
        const src = this.lineImage(fields.name, fields.image, fields.isDock);
        if (src && img.getAttribute("src") !== src) {
          img.src = src;
          img.alt = fields.name;
          img.style.visibility = "";
        }
      }
      this.tick(li.querySelector(".gdab__cart-qty"), String(fields.qty));
      const priceEl = li.querySelector(".gdab__price");
      if (priceEl) this.tick(priceEl, fields.price);
      if (wasEl && fields.was) this.tick(wasEl, fields.was);
      if (stockEl && fields.stock) stockEl.textContent = fields.stock;
      // the template's shell arrives empty (skeleton adopt): write it, or
      // the row shows a bare bullet dot with no words
      if (cancelEl && fields.cancel) cancelEl.textContent = fields.cancel;
      // the savings amount FOLLOWS the qty — a stale "$60" beside qty 2
      // misstates the deal (presence drift already forced a rebuild above)
      if (saveEl && fields.save && saveEl.textContent !== fields.save) {
        saveEl.textContent = fields.save;
      }
    }

    // provisional=true renders a view the server hasn't confirmed (the
    // optimistic add, a truth rollback): _lastCart — local TRUTH for the
    // fallback paths — is only ever set by confirmed carts.
    renderCart(cart, provisional) {
      if (!provisional) {
        this._lastCart = cart;
        this._truthSeq = (this._truthSeq || 0) + 1;
      }
      const lines = this.$(".gdab__cart-lines");
      // duplicate-keyed nodes (two lines that resolved to one placeholder
      // key) would silently collapse in the Map constructor — the dropped
      // node became an unreachable ghost line that no later render could
      // remove (QA P2-4). Keep the first, drop the ghost on sight.
      const existing = new Map();
      lines.querySelectorAll(".gdab__cart-line").forEach((n) => {
        if (existing.has(n.dataset.lineKey)) n.remove();
        else existing.set(n.dataset.lineKey, n);
      });
      (cart.items || []).forEach((item) => {
        const vid = String(item.variant_id || item.id);
        // Figma shows the UNIT price on the card (the line total lives in
        // Total): qty 2 at $149 must not read as a $298 product (QA find)
        const qty = item.quantity || 1;
        const unitCents = item.final_price ?? item.price
          ?? Math.round((item.final_line_price ?? item.line_price ?? 0) / qty);
        const compareCents = item.original_price || 0;
        const fields = {
          // placeholder lines (fallback add, no server key) key by VARIANT
          // id, never "" — "" collided distinct keyless lines onto one Map
          // slot (QA P2-4); /cart/change.js accepts a variant id, so the
          // steppers stay addressable in the degraded state too
          key: String(item.key || vid),
          name: this.noteMap[vid]?.name || item.product_title,
          ship: this.lineShipText(item),
          price: this.money(unitCents),
          was: compareCents > unitCents ? this.money(compareCents) : null,
          qty: item.quantity,
          image: item.image,
          isDock: this.isDockCartItem(item),
          stock: this.stockMap[vid] || "",
          // the pre-order reassurance bullet: noteMap (the checkout note's
          // metafield truth) says which variants pre-order; unknown lines
          // show nothing rather than guess, like the note itself
          cancel: this.lineCancelText(item),
          save: this.lineSaveText(vid, unitCents, qty),
        };
        const cur = existing.get(fields.key);
        if (cur) { this.updateLine(cur, fields); existing.delete(fields.key); }
        else lines.appendChild(this.lineEl(fields));
      });
      existing.forEach((n) => {
        // the pending/failed line is CLIENT-owned while an add is
        // unresolved: a truth render (rollback, a neighbouring qty
        // change) must not silently eat its progress/retry affordance
        if (this._pending && n.dataset.lineKey === this._pending.key) return;
        // a vanishing skeleton owns its exit: dissolve, then self-remove
        if (n.classList.contains("gdab__cart-line--vanish")) return;
        n.remove();                       // lines the server no longer has
      });
      // no design exists yet for the empty state (Jackson: built with the
      // real site) — this is the functional guard, not the design. A
      // visible pending/failed line vetoes the empty message: "your cart
      // is empty" beside a retry row contradicts itself.
      const empty = !(cart.items || []).length;
      const showEmpty = empty && !this._pending;
      this.cart.classList.toggle("is-empty", showEmpty);
      const emptyEl = this.$(".gdab__cart-empty");
      if (emptyEl) emptyEl.hidden = !showEmpty;
      this.renderTotals(cart);
      this.syncBadge(cart);
    }

    // Shopify's official Shop Pay component uses checkout-link variant
    // coordinates rather than the cart-only additional-checkout drop. Keep
    // the drawer's element exact after every Ajax add/change; replacement
    // is intentional because the component's upgrade lifecycle is safest
    // with immutable initialization attributes.
    syncCartShopPay(cart) {
      const express = this.$("[data-cart-shop-pay]");
      if (!express) return null;
      const variants = (cart.items || [])
        .filter((item) => (item.quantity || 0) > 0)
        .map((item) => `${item.variant_id || item.id}:${item.quantity}`)
        .join(",");
      const current = express.querySelector("shop-pay-button");

      if (!variants) {
        if (current) current.remove();
        express.hidden = true;
        return express;
      }

      express.hidden = false;
      const storeUrl = express.dataset.storeUrl;
      if (!storeUrl) return express;
      if (!current || current.getAttribute("variants") !== variants) {
        const next = document.createElement("shop-pay-button");
        next.setAttribute("store-url", storeUrl);
        next.setAttribute("variants", variants);
        if (current) current.replaceWith(next);
        else express.append(next);
      }
      return express;
    }

    // money row + checkout state, split from renderCart so a failed add
    // can roll totals back to server truth WITHOUT re-rendering lines
    renderTotals(cart) {
      const empty = !(cart.items || []).length;
      const checkout = this.$(".gdab__checkout");
      if (checkout) {
        // checkout stays dead while a line is pending (spec): the control
        // would race the very POST that gives it something to buy
        const dead = empty || this.cartMutationActive() || Boolean(this._changeRetry);
        checkout.classList.toggle("is-disabled", dead);
        // the wallet block below the CTA shares its dead state: express
        // buttons must not race a POST that changes what they'd buy.
        // pointer-events alone is POINTER-only — inert (with the
        // aria-hidden belt for pre-inert WebKit) also drops the wallet
        // out of the tab order and the AT tree (review find)
        const express = this.syncCartShopPay(cart);
        if (express) {
          express.classList.toggle("is-disabled", dead);
          if ("inert" in express) express.inert = dead;
          if (dead) express.setAttribute("aria-hidden", "true");
          else express.removeAttribute("aria-hidden");
        }
        if (dead) {
          checkout.disabled = true;
          checkout.setAttribute("aria-disabled", "true");
          checkout.setAttribute("tabindex", "-1");
        } else {
          checkout.disabled = false;
          checkout.removeAttribute("aria-disabled");
          checkout.removeAttribute("tabindex");
        }
      }
      this.renderCheckoutNote(cart);
      this.tick(this.$(".gdab__cart-total-value"), this.money(cart.total_price));
      const compareTotal = cart.original_total_price || cart.total_price;
      const totalWas = this.$(".gdab__cart-total-was");
      if (totalWas) {
        if (compareTotal > cart.total_price) {
          totalWas.hidden = false;
          this.tick(totalWas, this.money(compareTotal));
        } else {
          totalWas.hidden = true;
        }
      }
      const spi = this.$(".gdab__cart-spi");
      if (spi) {
        spi.textContent = (cart.total_price >= 5000 && cart.total_price <= 99999)
          ? this.t("spiLine").replace("%s", this.money(Math.round(cart.total_price / 4)))
          : "";
      }
    }

    renderLineShip(line, ship) {
      let label = line.querySelector(".gdab__cart-line-ship");
      if (!label && ship) {
        label = document.createElement("span");
        label.className = "gdab__cart-line-ship";
        line.querySelector(".gdab__cart-line-price")?.after(label);
      }
      if (label) {
        label.textContent = ship || "";
        label.hidden = !ship;
      }
    }

    renderCheckoutNote(cart) {
      const note = this.$("[data-cart-checkout-note]");
      if (!note) return;
      const items = Array.isArray(cart.items) ? cart.items : [];
      const entries = items.filter(item => !window.NexDock?.isDock(item)).map((item) =>
        this.noteMap[String(item.variant_id || item.id)] || null);
      const fullyKnown = entries.length > 0 && entries.every(Boolean);
      const preorders = fullyKnown ? entries.filter((entry) => entry.preorder) : [];
      const tails = preorders.map((entry) => String(entry.tail || "").trim());
      let tail = "";
      if (fullyKnown && preorders.length && tails.every(Boolean)) {
        tail = new Set(tails).size === 1 && preorders.length === entries.length
          ? tails[0] : (this.$('[data-t="mixedShipDates"]')?.textContent.trim() || "");
      } else if (fullyKnown && !preorders.length) {
        tail = String(entries[0].tail || "").trim();
      }
      const tailEl = note.querySelector("[data-checkout-note-tail]");
      if (tailEl && tailEl.textContent !== tail) tailEl.textContent = tail;
      note.hidden = !tail;
    }

    // Last-known cart + one unit of `variant` merged in. Serves BOTH the
    // optimistic pre-add view (added = null) and the refresh-failed view
    // after a successful add — merging beats wiping the sheet down to one
    // line with an under-reported total (QA find). Prices come from the
    // add response when present, else the card's display price. When an
    // optimistic add is live, the merged line's key is stamped onto
    // _pending so the pending dress lands on the right node.
    mergedCart(variant, added) {
      const cents = (v) => {
        const n = parseFloat(String(v || "").replace(/[^0-9.]/g, ""));
        return isNaN(n) ? 0 : Math.round(n * 100);
      };
      const unit = (added && (added.final_price ?? added.price)) || cents(variant.price);
      const prev = (this._lastCart && Array.isArray(this._lastCart.items))
        ? this._lastCart.items.map((i) => ({ ...i })) : [];
      const vid = String((added && (added.variant_id || added.id)) || variant.id);
      const key = (added && added.key) || "";
      const match = prev.find((i) =>
        (key && String(i.key) === String(key)) || String(i.variant_id || i.id) === vid);
      if (match) {
        match.quantity += 1;
        match.final_line_price = (match.final_price ?? match.price ?? unit) * match.quantity;
        if (this._pending) this._pending.key = String(match.key || vid);
      } else {
        prev.push({
          key, variant_id: Number(vid) || vid,
          product_title: (added && added.product_title) || variant.name,
          quantity: 1, price: unit, final_price: unit, final_line_price: unit,
          image: added && added.image,
        });
        if (this._pending) this._pending.key = String(key || vid);
      }
      return {
        items: prev,
        item_count: prev.reduce((a, i) => a + i.quantity, 0),
        total_price: prev.reduce((a, i) =>
          a + (i.final_line_price ?? (i.final_price ?? i.price ?? 0) * i.quantity), 0),
      };
    }
    // Cart view when /cart.js failed right after a successful add — the
    // merged view IS the best local truth then, so it renders confirmed.
    renderCartFallback(variant, added) {
      this.renderCart(this.mergedCart(variant, added));
    }

    syncBadge(cart) {
      const badge = document.querySelector("[data-cart-count]");
      if (badge && cart) this.tick(badge, String(cart.item_count));
    }

    // Vertical label ticker: old value rolls up and out, new rolls in from
    // below (0.2s). Text-only containers; safe to rebuild children each
    // call. dataset.tickText holds the logical value so a suspended
    // animation can never corrupt the comparison.
    tick(el, text, fill) {
      if (!el) return;
      const cur = el.dataset.tickText != null ? el.dataset.tickText : el.textContent.trim();
      if (cur === text) return;
      el.dataset.tickText = text;
      if (this.reducedMotion() || !el.isConnected || !cur) {
        clearTimeout(el.__gdabTickT);
        el.style.width = "";             // a prior roll may have left a lock
        el.replaceChildren();
        if (fill) fill(el); else el.textContent = text;
        return;
      }
      const w0 = el.offsetWidth;             // lock the outgoing width first
      el.classList.add("gdab__tick");
      const out = document.createElement("span");
      out.className = "gdab__tick-out";
      out.setAttribute("aria-hidden", "true");
      // clone the LIVE content (not textContent) so nested spans keep
      // their styling (e.g. the hidden "with Shop" tail). If a roll is
      // still in flight (rapid taps), the current TRUTH is the tick-in
      // span — cloning everything would duplicate the stale out-span.
      const liveIn = el.querySelector(":scope > .gdab__tick-in");
      const src = liveIn ? liveIn.childNodes : el.childNodes;
      out.append(...[...src].map((n) => n.cloneNode(true))
        .filter((n) => !(n.classList && n.classList.contains("gdab__tick-out"))));
      const inn = document.createElement("span");
      inn.className = "gdab__tick-in";
      if (fill) fill(inn); else inn.textContent = text;
      el.replaceChildren(out, inn);
      // width glides old→new DURING the roll, so neighbors settle with the
      // animation instead of jumping when the old span is removed
      el.style.width = w0 + "px";
      void el.offsetWidth;
      el.style.width = inn.offsetWidth + "px";
      // belt (QA P2-5): animationend can stall in a hidden/suspended tab —
      // without it the fixed inline width stayed locked forever and the
      // cell could never re-flow. Token-guarded so a roll that started
      // AFTER this one can never have its width glide wiped by a stale
      // settle firing late.
      const token = (el.__gdabTickN = (el.__gdabTickN || 0) + 1);
      const settle = () => {
        if (el.__gdabTickN !== token) return;
        clearTimeout(el.__gdabTickT);
        if (out.parentNode) out.remove();
        el.classList.remove("gdab__tick");
        el.style.width = "";                 // auto == the new width: no shift
      };
      out.addEventListener("animationend", settle, { once: true });
      clearTimeout(el.__gdabTickT);
      el.__gdabTickT = setTimeout(settle, 700);   // 200ms roll + generous slack
    }

    // --------------------------------------------------------------- utils
    // Handles every documented shop.money_format placeholder; unknown future
    // placeholders degrade to the standard two-decimal rendering rather than
    // leaking "{{amount_x}}" into the UI.
    money(cents) {
      const n = cents / 100;
      const group = (s) => s.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      const groupDot = (s) => s.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
      return this.cfg.moneyFmt.replace(/\{\{\s*(\w+)\s*\}\}/g, (m, key) => {
        switch (key) {
          case "amount": return group(n.toFixed(2)).replace(/\.00$/, "");   // design: whole dollars bare
          case "amount_no_decimals": return group(String(Math.round(n)));
          case "amount_with_comma_separator":
            return groupDot(n.toFixed(2)).replace(/\.(\d\d)$/, ",$1");
          case "amount_no_decimals_with_comma_separator":
            return groupDot(String(Math.round(n)));
          case "amount_with_apostrophe_separator":
            return n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, "'");
          default: return group(n.toFixed(2));
        }
      });
    }
    t(key) {
      const el = this.querySelector(`[data-t="${key}"]`);
      return el ? el.textContent.trim() : key;
    }
    // NOT t(): a missing span must yield "", never the literal key — stale
    // cached section HTML paired with this newer file would otherwise
    // print "cancelNote" on every pre-order line (the cache-pairing class)
    cancelNoteText() {
      return this.querySelector('[data-cart-copy="cancel"]')?.textContent.trim() || "";
    }
    lineShipText(item) {
      if (this.isDockCartItem(item)) return "";
      const entry = this.noteMap[String(item.variant_id || item.id)];
      if (!entry) return "";
      if (!entry.preorder) return this.querySelector('[data-cart-copy="available"]')?.textContent.trim() || "";
      const label = this.querySelector('[data-cart-copy="preorder"]')?.textContent.trim() || "";
      return [label, String(entry.tail || "").trim()].filter(Boolean).join(" · ");
    }
    lineCancelText(item) {
      if (this.isDockCartItem(item)) {
        const dock = window.NexDock;
        const free = typeof dock?.isFree === "function" ? dock.isFree(item) : item.final_line_price === 0;
        if (free && dock?.config.copy.giftIncluded) return dock.config.copy.giftIncluded;
        const el = this.querySelector('[data-t="dockAddonOnly"]');
        return el ? el.textContent.trim() : "";
      }
      const vid = String(item?.variant_id || item?.id || "");
      return this.noteMap[vid]?.preorder ? this.cancelNoteText() : "";
    }
    lineSaveText() {
      return "";
    }
    // inert removes the opacity-0 pill from the a11y tree without touching
    // the visual crossfade (visibility would cut the morph mid-fade)
    setPillExposed(exposed) {
      if ("inert" in this.pill) this.pill.inert = !exposed;
      else if (exposed) this.pill.removeAttribute("aria-hidden");
      else this.pill.setAttribute("aria-hidden", "true");
    }
    announce(msg) {
      // always the root region: it lives outside both dialogs, is never
      // inert, and is visible regardless of which surface is mid-morph
      if (this.liveRoot) this.liveRoot.textContent = msg;
    }
    emit(name, detail) {
      this.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
    }
  }

  customElements.define("nex-gdab", NexGdab);
})();



